// Project Detail page translations
export const projectDetailTranslations = {
  en: {
    "tutorial": {
      "title": "Getting started",
      "step1Title": "Set up your AI interviewer",
      "step1Body": "This is where you configure the AI that interviews your participants — its questions, persona, and how it follows up.",
      "step2Title": "Choose voice or text & language",
      "step2Body": "Pick how participants respond — text, voice-to-text, or real-time voice — and set the AI voice and interview language.",
      "step3Title": "Copy & share your link",
      "step3Body": "Share your interview link with participants from the Share Links section. The AI conducts each interview and transcribes it automatically.",
      "step4Title": "Watch results come in",
      "step4Body": "As participants finish, open Analytics to review transcripts and themes — or send them to the QDA Workspace for deeper coding.",
      "back": "Back",
      "next": "Next",
      "done": "Get started",
      "skip": "Skip",
      "help": "How it works"
    },
    "loading": "Loading...",
    "sections": {
      "overview": "Project Overview",
      "outline": "Interview Outline",
      "participants": "Participants",
      "data": "Interview Data",
      "settings": "Project Settings",
      "title": "API Configuration",
      "recordingOptions": "Recording Options",
      "interviewSettings": "Interview Settings",
      "generalSettings": "General Settings",
      "standardModeSettings": "Standard Mode Settings",
      "outlineHelp": "Need help writing your outline? View Interview Guide",
      "customForm": "Custom Interview Form",
      "projectSettings": "Project Settings",
      "contentConfiguration": "Content Configuration", 
      "dataAnalytics": "Data & Analytics",
      "customFormDescription": "Create a custom form to collect additional information from interviewees before the interview begins.",
      "shareLinks": "Share Interview Links",
      "navigation": "Navigation",
      "digitalTwinConsent": "Collect Digital Twin Consent",
      "digitalTwinConsentDescription": "When enabled, participants will be asked if they consent to their interview data being used to create an AI persona (digital twin) for future research."
    },
    "formBuilder": {
      "title": "Form Builder",
      "previewForm": "Preview Form",
      "saveForm": "Save Form",
      "saving": "Saving...",
      "formFields": "Form Fields",
      "noFieldsTitle": "No custom fields added yet",
      "noFieldsDesc1": "Leave this blank to use the default form (name, age, gender, occupation, education, contact)",
      "noFieldsDesc2": "Or click on any field type above to create a custom interview form",
      "addFirstField": "Add Your First Field",
      "categories": {
        "input": "Input Fields",
        "choice": "Choice Fields",
        "display": "Display Elements"
      },
      "fieldTypes": {
        "text": "Text Input",
        "textarea": "Long Text", 
        "email": "Email",
        "phone": "Phone",
        "number": "Number",
        "date": "Date",
        "file": "File Upload",
        "select": "Dropdown",
        "radio": "Radio Buttons",
        "checkbox": "Checkboxes",
        "display_text": "Text Block",
        "display_image": "Image",
        "display_video": "Video",
        "section_header": "Section Header",
        "page_break": "Page Break"
      },
      "fieldDescriptions": {
        "text": "Single line text input",
        "textarea": "Multi-line text area",
        "email": "Email address input",
        "phone": "Phone number input", 
        "number": "Numeric input",
        "date": "Date picker",
        "file": "File upload field",
        "select": "Dropdown selection",
        "radio": "Single choice from options",
        "checkbox": "Multiple choice options",
        "display_text": "Display text or HTML",
        "display_image": "Display an image",
        "display_video": "Embed video content",
        "section_header": "Section title",
        "page_break": "Start new page"
      },
      "fieldEditor": {
        "title": "Field Properties",
        "fieldLabel": "Field Label",
        "fieldType": "Field Type",
        "placeholderText": "Placeholder Text",
        "placeholderHelp": "Placeholder text is shown inside the input field as a hint for users. It disappears when they start typing.",
        "helpText": "Help Text",
        "helpTextHelper": "Optional helper text to guide users",
        "required": "Required Field",
        "options": "Options",
        "addOption": "Add Option",
        "removeOption": "Remove Option",
        "imageUrl": "Image URL",
        "altText": "Alt Text",
        "videoUrl": "Video URL",
        "videoUrlHelper": "YouTube, Vimeo, or direct video file URL",
        "displayContent": "Display Content",
        "displayContentHelper": "Supports basic HTML tags",
        "textareaRows": "Number of Rows",
        "fileAccept": "Accepted File Types",
        "maxFileSize": "Max File Size (MB)"
      },
      "defaults": {
        "displayTextContent": "Your display text here...",
        "sectionTitle": "Section Title"
      },
      "validation": {
        "emailAutoApplied": "Email validation will be automatically applied",
        "numberAutoApplied": "Number validation will be automatically applied", 
        "phoneAutoApplied": "Phone number validation will be automatically applied"
      },
      "actions": {
        "duplicate": "Duplicate",
        "moveUp": "Move Up", 
        "moveDown": "Move Down",
        "delete": "Delete",
        "close": "Close"
      },
      "labels": {
        "untitledField": "Untitled Field",
        "required": "Required",
        "newField": "New"
      }
    },    "fields": {
      "outlineTitle": "Interview Outline",
      "apiConfig": "API Configuration",
      "participantInfo": "Participant Information",
      "recordAudio": "Record Audio",
      "hideIntervieweeTranscript": "Hide interviewee's transcribed text",
      "hideIntervieweeTranscriptHint": "When enabled, interviewees will only see the AI interviewer's messages, not their own transcribed speech.",
      "recordVideo": "Record Video",
      "voiceEnabled": "AI Voice Interviewer",
      "interactionMode": "Interaction Mode",
      "aiVoice": "AI Voice",
      "maxDuration": "Maximum Interview Duration",
      "name": "Name",
      "age": "Age",
      "gender": "Gender",
      "occupation": "Occupation",
      "education": "Education",
      "interviewType": "Interview Type",
      "language": "Language",
      "transcriptionEngine": "Transcription Engine",
      "aiInterviewerName": "AI Interviewer Name",
      "aiInterviewerNamePlaceholder": "e.g., Dr. Smith, Research Assistant",
      "aiInterviewerNameHelper": "Leave empty to use the default name based on interview language",
      "interviewerMode": "AI Interviewer Mode",
      "interviewerModeHelp": "Choose how many AI models drive this interview.",
      "probingDepth": "Probing Depth",
      "probingDepthHelp": "How aggressively the AI follows up on each answer."
    },
    "probingDepth": {
      "lightLabel": "Light",
      "lightDesc": "0-1 follow-ups · prioritizes coverage · fast pace",
      "moderateLabel": "Moderate (recommended)",
      "moderateDesc": "1-2 follow-ups · balanced depth and coverage",
      "deepLabel": "Deep",
      "deepDesc": "2-4 follow-ups · drills into motivations and examples"
    },
    "interviewerModes": {
      "singleLabel": "Single Model (faster)",
      "singleDesc": "One AI handles the full interview. Lower latency, lower cost.",
      "dualLabel": "Dual Model (recommended)",
      "dualDesc": "A supervisor model audits the responder for higher-quality follow-ups.",
      "structuredNotice": "Structured interviews always use the dual-model system."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (recommended, best accuracy)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — best multilingual accuracy across 99 languages. Auto-falls-back to Google → Whisper → Xunfei if needed.",
      "autoSelectedElevenLabs": "✓ ElevenLabs auto-selected",
      "voicePicker": {
        "autoLabel": "Auto (Recommended)",
        "autoDesc": "ElevenLabs Bella · multilingual female · all 10 languages",
        "warmFemaleMultilingual": "Warm female · multilingual",
        "calmFemaleMultilingual": "Calm female · multilingual",
        "youngFemaleMultilingual": "Young female · multilingual",
        "matureMaleMultilingual": "Mature male · multilingual",
        "warmMaleMultilingual": "Warm male · multilingual",
        "casualMaleMultilingual": "Casual male · multilingual",
        "mimoDesc": "Native Chinese female · best for Mandarin",
        "openaiDesc": "Neutral voice · multilingual",
        "xunfeiDesc": "Chinese female (legacy)",
        "recommendedBadge": "RECOMMENDED",
        "orPickSpecific": "Or pick a specific voice:",
        "filterAll": "All voices",
        "filterFemale": "Female",
        "filterMale": "Male",
        "filterChinese": "Best for Chinese",
        "noMatch": "No voices match this filter."
      },
      "google": "Google Speech (recommended, best accuracy)",
      "whisper": "Whisper (fast, good accuracy)",
      "xunfei": "Xunfei (slow, no longer recommended)",
      "helperChinese": "Google Speech (recommended) — strongest Mandarin accuracy. Whisper is a faster, cheaper alternative. Xunfei is kept as a fallback only.",
      "helperDefault": "Whisper (recommended) — fast and cost-effective. Google Speech offers higher accuracy at higher cost.",
      "autoSelectedXunfei": "✓ Xunfei auto-selected for Chinese",
      "autoSelectedGoogle": "✓ Google auto-selected for Chinese",
      "autoSelectedWhisper": "✓ Whisper auto-selected"
    },
    "interactionModes": {
      "textToText": "Text to Text",
      "textToVoice": "Text to Voice + Text",
      "voiceToText": "Voice to Text",
      "voiceToVoice": "Voice to Voice + Text",
      "textToTextDesc": "Type your answers, read AI responses (No audio)",
      "textToVoiceDesc": "Type your answers, AI responds with voice + text",
      "voiceToTextDesc": "Speak your answers, read AI responses (No audio)",
      "voiceToVoiceDesc": "Speak your answers, AI responds with voice + text"
    },    "actions": {
      "saveOutline": "Save Outline",
      "updateApiKey": "Update API Key",
      "downloadData": "Download Interview Data",
      "liveSupervision": "Live Supervision",
      "downloadCsv": "Download CSV",
      "deleteParticipant": "Delete Participant",
      "addKey": "Add API Key",
      "saveRecordingPreferences": "Save Recording Preferences",
      "saveInterviewSettings": "Save Interview Settings",
      "saveGeneralSettings": "Save General Settings",
      "saveStandardModeSettings": "Save Standard Mode Settings",
      "viewInterviewGuide": "View Interview Guide",
      "editProjectName": "Edit Project Name",
      "findParticipants": "Find Participants",
      "testWithSynthetic": "Test with Synthetic Human",
      "classicView": "Classic View",
      "newView": "New Layout"
    },
    "interviewTypes": {
      "structured": "Structured Interview",
      "semiStructured": "Semi-Structured Interview"
    },
    "languages": {
      "english": "English",
      "chinese": "Chinese",
      "french": "French (Français)",
      "norwegian": "Norwegian (Norsk)",
      "dutch": "Dutch (Nederlands)",
      "german": "German (Deutsch)",
      "spanish": "Spanish (Español)",
      "portuguese": "Portuguese (Português)",
      "japanese": "Japanese (日本語)",
      "arabic": "Arabic (العربية)",
      "flexible": "Flexible",
      "flexibleHelp": "Let your interviewee select their preferred language"
    },
    "messages": {
      "outlineSaved": "Outline saved successfully",
      "outlineSaveFailed": "Failed to save outline",
      "saveFailed": "Failed to save outline",
      "apiKeyUpdated": "API Key updated successfully",
      "apiKeyError": "Failed to update API Key",
      "downloadStarted": "Download started",
      "downloadComplete": "Download completed",
      "downloadFailed": "Download failed",
      "noParticipants": "No participants found",
      "deleteConfirm": "Are you sure you want to delete this participant?",
      "deleteSuccess": "Participant deleted successfully",
      "deleteFailed": "Failed to delete participant",
      "notFound": "Project not found",
      "noKey": "API Key not configured",
      "settingsSaved": "Interview settings saved successfully",
      "settingsScopeNote": "These settings only apply to the Standard Mode interview link. The Realtime Audio (experimental) link has its own voice, response pacing, and interruption settings (in the Real-time Voice Mode panel above); interaction mode, transcription engine, interviewer mode, probing depth, and AI interviewer name apply to Standard Mode only.",
      "generalSettingsScopeNote": "These apply to both the Standard Mode and the Realtime Audio (experimental) interview links.",
      "settingsError": "Failed to save interview settings",
      "loadError": "Failed to load project details, please try again later",
      "timeoutError": "Request timeout - please check your connection",
      "fetchError": "Failed to load project details", 
      "participantsTimeout": "Failed to load participant data due to timeout, please check your connection",
      "participantsError": "Failed to fetch participants",
      "participantsPagination": "Total {{total}} participants, page {{page}} of {{pages}}",
      "dataVisualization": "Data Exploration",
      "themeAnalysisLens": "Theme analysis using ThemeLens",
      "inductiveCoding": "AI Inductive Coding",
      "optimizeOutline": "AI Optimize Outline",
      "optimizationResult": "Optimization Result",
      "optimizedOutline": "Optimized Outline:",
      "improvements": "Key Improvements:",
      "suggestions": "Suggestions:",
      "applyOptimization": "Apply Optimization",
      "startOptimization": "Start Optimization",
      "optimizationGoals": "Optimization Goals (Optional)",
      "optimizationGoalsPlaceholder": "e.g.: Increase question depth, improve question openness, optimize question logic, etc.",
      "unsavedChanges": "You have unsaved changes",
      "changesDiscarded": "Changes discarded",
      "optimizing": "Optimizing...",
      "recordingPreferencesSaved": "Recording preferences saved",
      "recordingPreferencesSaveFailed": "Failed to save recording preferences",
      "outlineRequired": "Outline is required",
      "outlineOptimized": "Outline optimized",
      "outlineOptimizeFailed": "Outline optimization failed",
      "optimizedOutlineApplied": "Optimized outline applied",
      "projectNameSaved": "Project name saved successfully",
      "projectNameSaveFailed": "Failed to save project name",
      "unsavedProjectNameChanges": "You have unsaved changes to the project name",
      "shareLinkInfo": "Share these links with your interviewees. They can access the interview directly without login.",
      "standardLinkCopied": "Standard mode link copied to clipboard",
      "realtimeLinkCopied": "Realtime mode link copied to clipboard",
      "selectInterviewFirst": "Please select an interview first."
    },
    "downloads": {
      "title": "Download options",
      "description": "Choose what you want to download:",
      "downloadEverything": "Download everything",
      "downloadMetadataOnly": "Download metadata only",
      "downloadAudioForSpecificInterview": "Download audio for a specific interview",
      "interviewLabel": "Interview",
      "downloadSelectedInterviewAudio": "Download selected interview audio"
    },
    "warnings": {
      "audioRecordingEnabled": "Warning: Audio recording is enabled for this project. Participants' voices will be recorded during interviews.",
      "audioInterviewNotice": "⚠️ Experimental feature: This is a new technology with lower accuracy."
    },
    "buttons": {
      "testWithSynthetic": "Test Interview with Synthetic Human"
    },
    "linkTypes": {
      "standardMode": "Standard Interview Mode",
      "standardDescription": "Supports 4 modalities (voice-to-voice, voice-to-text, text-to-voice, text-to-text). Recommended.",
      "realtimeMode": "Realtime Voice Mode (Experimental)",
      "realtimeDescription": "Voice-based interview with real-time AI conversation. New technology."
    },
    "realtimeSettings": {
      "title": "Real-time voice settings",
      "description": "Customize the real-time audio interview. Defaults match the previous version.",
      "voiceLabel": "AI voice",
      "voiceDefaultTag": "default",
      "voiceNaturalTag": "more natural",
      "pacingLabel": "Response pacing",
      "pacingQuick": "Quick — responds fast",
      "pacingStandard": "Standard (default)",
      "pacingPatient": "Patient — waits longer",
      "pacingHelp": "How long the AI waits after the participant stops speaking before it replies.",
      "allowInterruptionLabel": "Allow participants to interrupt the AI",
      "allowInterruptionHelp": "On: participants can talk over the AI. Off: the AI finishes speaking before it listens — prevents it being cut off by echo from laptop speakers.",
      "save": "Save real-time settings",
      "saved": "Real-time settings saved",
      "saveFailed": "Failed to save real-time settings"
    },
    "currentSettings": "Current settings: {{type}}, Language: {{language}}",
    "collaboration": {
      "title": "Team",
      "invite": "Invite Collaborator",
      "inviteLabel": "Email address",
      "inviteButton": "Invite",
      "inviting": "Sending...",
      "inviteSuccess": "Invitation sent!",
      "inviteError": "Failed to send invitation. Please try again.",
      "alreadyInvited": "This email has already been invited.",
      "collaborators": "Collaborators",
      "noCollaborators": "No collaborators yet. Invite someone to get started.",
      "statusPending": "Pending",
      "statusActive": "Active",
      "remove": "Remove",
      "removeConfirm": "Remove this collaborator?",
      "sharedBy": "Shared by {{owner}}",
      "surveyHint": "Invite people to this project. Collaborators can view and edit all of its surveys.",
      "close": "Close"
    }
  },
  zh: {
    "tutorial": {
      "title": "快速入门",
      "step1Title": "设置你的 AI 访谈员",
      "step1Body": "在这里配置负责访谈参与者的 AI——它的问题、人设以及追问方式。",
      "step2Title": "选择语音或文本及语言",
      "step2Body": "选择参与者的作答方式——文本、语音转文本或实时语音——并设置 AI 语音和访谈语言。",
      "step3Title": "复制并分享你的链接",
      "step3Body": "在「分享链接」区域将访谈链接分享给参与者。AI 会进行每一次访谈并自动转写。",
      "step4Title": "查看陆续到来的结果",
      "step4Body": "随着参与者陆续完成，打开「分析」查看转写与主题——或将其发送至 QDA 工作区进行更深入的编码。",
      "back": "上一步",
      "next": "下一步",
      "done": "开始使用",
      "skip": "跳过",
      "help": "工作原理"
    },
    "loading": "加载中...",
    "sections": {
      "overview": "项目概览",
      "outline": "访谈大纲",
      "participants": "参与者",
      "data": "访谈数据",
      "settings": "项目设置",
      "title": "API 配置",
      "recordingOptions": "录制选项",
      "interviewSettings": "访谈设置",
      "generalSettings": "通用设置",
      "standardModeSettings": "标准模式设置",
      "outlineHelp": "需要帮助写访谈大纲？查看访谈指南",
      "customForm": "自定义访谈表单",
      "projectSettings": "项目设置",
      "contentConfiguration": "内容配置", 
      "dataAnalytics": "数据与分析",
      "customFormDescription": "创建自定义表单，在访谈开始前收集受访者的额外信息。",
      "shareLinks": "分享访谈链接",
      "navigation": "导航",
      "digitalTwinConsent": "收集数字孪生同意",
      "digitalTwinConsentDescription": "启用后，参与者将被询问是否同意将其访谈数据用于创建AI人物（数字孪生）以供未来研究使用。"
    },
    "formBuilder": {
      "title": "表单构建器",
      "previewForm": "预览表单",
      "saveForm": "保存表单",
      "saving": "保存中...",
      "formFields": "表单字段",
      "noFieldsTitle": "尚未添加自定义字段",
      "noFieldsDesc1": "留空以使用默认表单（姓名、年龄、性别、职业、教育背景、联系方式）",
      "noFieldsDesc2": "或点击上方任意字段类型创建自定义访谈表单",
      "addFirstField": "添加您的第一个字段",
      "categories": {
        "input": "输入字段",
        "choice": "选择字段",
        "display": "显示元素"
      },
      "fieldTypes": {
        "text": "文本输入",
        "textarea": "长文本", 
        "email": "电子邮件",
        "phone": "电话",
        "number": "数字",
        "date": "日期",
        "file": "文件上传",
        "select": "下拉菜单",
        "radio": "单选按钮",
        "checkbox": "复选框",
        "display_text": "文本块",
        "display_image": "图片",
        "display_video": "视频",
        "section_header": "章节标题",
        "page_break": "分页符"
      },
      "fieldDescriptions": {
        "text": "单行文本输入",
        "textarea": "多行文本区域",
        "email": "电子邮件地址输入",
        "phone": "电话号码输入", 
        "number": "数字输入",
        "date": "日期选择器",
        "file": "文件上传字段",
        "select": "下拉选择",
        "radio": "从选项中单选",
        "checkbox": "多选选项",
        "display_text": "显示文本或HTML",
        "display_image": "显示图片",
        "display_video": "嵌入视频内容",
        "section_header": "章节标题",
        "page_break": "开始新页面"
      },
      "fieldEditor": {
        "title": "字段属性",
        "fieldLabel": "字段标签",
        "fieldType": "字段类型",
        "placeholderText": "占位符文本",
        "placeholderHelp": "占位符文本显示在输入框内作为用户提示，用户开始输入时会消失。",
        "helpText": "帮助文本",
        "helpTextHelper": "可选的帮助文本来指导用户",
        "required": "必填字段",
        "options": "选项",
        "addOption": "添加选项",
        "removeOption": "删除选项",
        "imageUrl": "图片URL",
        "altText": "替代文本",
        "videoUrl": "视频URL",
        "videoUrlHelper": "YouTube、Vimeo或直接视频文件URL",
        "displayContent": "显示内容",
        "displayContentHelper": "支持基本HTML标签",
        "textareaRows": "行数",
        "fileAccept": "接受的文件类型",
        "maxFileSize": "最大文件大小（MB）"
      },
      "defaults": {
        "displayTextContent": "在此输入您的显示文本...",
        "sectionTitle": "章节标题"
      },
      "validation": {
        "emailAutoApplied": "将自动应用电子邮件验证",
        "numberAutoApplied": "将自动应用数字验证", 
        "phoneAutoApplied": "将自动应用电话号码验证"
      },
      "actions": {
        "duplicate": "复制",
        "moveUp": "上移", 
        "moveDown": "下移",
        "delete": "删除",
        "close": "关闭"
      },
      "labels": {
        "untitledField": "未命名字段",
        "required": "必填",
        "newField": "新"
      }
    },    "fields": {
      "outlineTitle": "访谈大纲",
      "apiConfig": "API 配置",
      "participantInfo": "参与者信息",
      "recordAudio": "录制音频",
      "hideIntervieweeTranscript": "隐藏受访者的转录文本",
      "hideIntervieweeTranscriptHint": "启用后，受访者在访谈中只会看到AI访谈员的消息，不会看到自己的语音转录文本。",
      "recordVideo": "录制视频",
      "voiceEnabled": "AI语音访谈员",
      "interactionMode": "交互模式",
      "aiVoice": "AI 语音",
      "maxDuration": "最长访谈时间",
      "name": "姓名",
      "age": "年龄",
      "gender": "性别",
      "occupation": "职业",
      "education": "教育背景",
      "interviewType": "访谈类型",
      "language": "语言",
      "transcriptionEngine": "转写引擎",
      "aiInterviewerName": "AI 访谈员名称",
      "aiInterviewerNamePlaceholder": "例如：张博士、研究助理",
      "aiInterviewerNameHelper": "留空将根据访谈语言使用默认名称",
      "interviewerMode": "AI 访谈员模式",
      "interviewerModeHelp": "选择本次访谈使用单模型还是双模型。",
      "probingDepth": "追问深度",
      "probingDepthHelp": "AI 对每个回答的追问积极度。"
    },
    "probingDepth": {
      "lightLabel": "浅",
      "lightDesc": "0-1 次追问 · 优先覆盖大纲 · 节奏快",
      "moderateLabel": "中等（推荐）",
      "moderateDesc": "1-2 次追问 · 平衡深度和覆盖",
      "deepLabel": "深入",
      "deepDesc": "2-4 次追问 · 深挖动机与具体例子"
    },
    "interviewerModes": {
      "singleLabel": "单模型（更快）",
      "singleDesc": "由一个 AI 完成整场访谈，延迟更低、成本更低。",
      "dualLabel": "双模型（推荐）",
      "dualDesc": "由监督模型审查响应模型的输出，追问质量更高。",
      "structuredNotice": "结构化访谈始终使用双模型系统。"
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2（推荐，最高准确度）",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — 在 99 种语言上拥有最佳的多语言识别准确度。如失败将自动回退至 Google → Whisper → 讯飞。",
      "autoSelectedElevenLabs": "✓ 已自动选择 ElevenLabs",
      "voicePicker": {
        "autoLabel": "自动（推荐）",
        "autoDesc": "ElevenLabs Bella · 多语种女声 · 支持全部 10 种语言",
        "warmFemaleMultilingual": "温暖女声 · 多语种",
        "calmFemaleMultilingual": "平静女声 · 多语种",
        "youngFemaleMultilingual": "年轻女声 · 多语种",
        "matureMaleMultilingual": "成熟男声 · 多语种",
        "warmMaleMultilingual": "温暖男声 · 多语种",
        "casualMaleMultilingual": "随性男声 · 多语种",
        "mimoDesc": "原生中文女声 · 最适合普通话",
        "openaiDesc": "中性嗓音 · 多语种",
        "xunfeiDesc": "中文女声（旧版）",
        "recommendedBadge": "推荐",
        "orPickSpecific": "或选择特定语音：",
        "filterAll": "全部",
        "filterFemale": "女声",
        "filterMale": "男声",
        "filterChinese": "中文优选",
        "noMatch": "没有符合此筛选条件的语音。"
      },
      "google": "Google Speech（推荐，最高准确度）",
      "whisper": "Whisper（快速，准确度良好）",
      "xunfei": "讯飞（速度慢，不再推荐）",
      "helperChinese": "推荐使用 Google Speech，普通话准确度最高。Whisper 更快且成本更低。讯飞仅作为备选。",
      "helperDefault": "推荐使用 Whisper，速度快、成本低。Google Speech 准确度更高，但费用也更高。",
      "autoSelectedXunfei": "✓ 已为中文自动选择讯飞",
      "autoSelectedGoogle": "✓ 已为中文自动选择 Google",
      "autoSelectedWhisper": "✓ 已自动选择 Whisper"
    },
    "interactionModes": {
      "textToText": "文本到文本",
      "textToVoice": "文本到语音+文本",
      "voiceToText": "语音到文本",
      "voiceToVoice": "语音到语音+文本",
      "textToTextDesc": "打字回答，阅读AI回复（无语音）",
      "textToVoiceDesc": "打字回答，AI用语音+文本回复",
      "voiceToTextDesc": "说话回答，阅读AI回复（无语音）",
      "voiceToVoiceDesc": "说话回答，AI用语音+文本回复"
    },    "actions": {
      "saveOutline": "保存大纲",
      "updateApiKey": "更新 API 密钥",
      "downloadData": "下载访谈数据",
      "liveSupervision": "实时监督",
      "downloadCsv": "下载 CSV",
      "deleteParticipant": "删除参与者",
      "addKey": "添加 API 密钥",
      "saveRecordingPreferences": "保存录制偏好",
      "saveInterviewSettings": "保存访谈设置",
      "saveGeneralSettings": "保存通用设置",
      "saveStandardModeSettings": "保存标准模式设置",
      "viewInterviewGuide": "查看访谈指南",
      "editProjectName": "编辑项目名称",
      "findParticipants": "寻找受访者",
      "testWithSynthetic": "使用模拟人测试",
      "classicView": "经典视图",
      "newView": "新布局"
    },
    "interviewTypes": {
      "structured": "结构化访谈",
      "semiStructured": "半结构化访谈"
    },
    "languages": {
      "english": "英语 (English)",
      "chinese": "中文 (Chinese)",
      "french": "法语 (Français)",
      "norwegian": "挪威语 (Norsk)",
      "dutch": "荷兰语 (Dutch)",
      "german": "德语 (Deutsch)",
      "spanish": "西班牙语 (Español)",
      "portuguese": "葡萄牙语 (Português)",
      "japanese": "日语 (日本語)",
      "arabic": "阿拉伯语 (العربية)",
      "flexible": "灵活",
      "flexibleHelp": "让您的受访者选择他们偏好的语言"
    },
    "messages": {
      "outlineSaved": "大纲保存成功",
      "outlineSaveFailed": "大纲保存失败",
      "saveFailed": "大纲保存失败",
      "apiKeyUpdated": "API 密钥更新成功",
      "apiKeyError": "API 密钥更新失败",
      "downloadStarted": "开始下载",
      "downloadComplete": "下载完成",
      "downloadFailed": "下载失败",
      "noParticipants": "未找到参与者",
      "deleteConfirm": "确定要删除此参与者吗？",
      "deleteSuccess": "参与者删除成功",
      "deleteFailed": "参与者删除失败",
      "notFound": "未找到项目",
      "noKey": "未配置 API 密钥",
      "settingsSaved": "访谈设置保存成功",
      "settingsScopeNote": "这些设置仅适用于标准模式（Standard Mode）访谈链接。实时语音（Realtime Audio，实验性）链接拥有独立的语音、回复节奏和打断设置（位于上方的\"实时语音模式\"面板中）；交互模式、转写引擎、访谈员模式、追问深度以及 AI 访谈员名称仅适用于标准模式（Standard Mode）。",
      "generalSettingsScopeNote": "这些设置同时适用于标准模式和实时音频（实验模式）访谈链接。",
      "settingsError": "访谈设置保存失败",
      "loadError": "无法加载项目详情，请稍后重试",
      "timeoutError": "请求超时，请检查网络连接",
      "fetchError": "加载项目详情失败",
      "participantsTimeout": "获取参与者数据超时，请检查网络连接",
      "participantsError": "获取参与者失败",
      "participantsPagination": "总计 {{total}} 个参与者，第 {{page}} 页，共 {{pages}} 页",
      "dataVisualization": "数据探索",
      "themeAnalysisLens": "使用 ThemeLens 进行主题分析",
      "inductiveCoding": "AI归纳编码",
      "optimizeOutline": "AI优化提纲",
      "optimizationResult": "优化结果",
      "optimizedOutline": "优化后的提纲：",
      "improvements": "主要改进点：",
      "suggestions": "建议：",
      "applyOptimization": "应用优化结果",
      "startOptimization": "开始优化",
      "optimizationGoals": "优化目标（可选）",
      "optimizationGoalsPlaceholder": "例如：增加问题深度、提高问题开放性、优化问题逻辑等",
      "unsavedChanges": "您有未保存的更改",
      "changesDiscarded": "更改已丢弃",
      "optimizing": "优化中...",
      "recordingPreferencesSaved": "录制偏好已保存",
      "recordingPreferencesSaveFailed": "录制偏好保存失败",
      "outlineRequired": "需要提纲",
      "outlineOptimized": "提纲已优化",
      "outlineOptimizeFailed": "提纲优化失败",
      "optimizedOutlineApplied": "已应用优化的提纲",
      "projectNameSaved": "项目名称保存成功",
      "projectNameSaveFailed": "项目名称保存失败",
      "unsavedProjectNameChanges": "您有未保存的项目名称更改",
      "shareLinkInfo": "与您的受访者分享这些链接。他们可以直接访问访谈，无需登录。",
      "standardLinkCopied": "标准模式链接已复制到剪贴板",
      "realtimeLinkCopied": "实时模式链接已复制到剪贴板",
      "selectInterviewFirst": "请先选择一个访谈。"
    },
    "downloads": {
      "title": "下载选项",
      "description": "请选择要下载的内容：",
      "downloadEverything": "下载全部",
      "downloadMetadataOnly": "仅下载元数据",
      "downloadAudioForSpecificInterview": "下载指定访谈的音频",
      "interviewLabel": "访谈",
      "downloadSelectedInterviewAudio": "下载所选访谈音频"
    },
    "warnings": {
      "audioRecordingEnabled": "警告：此项目已启用录音功能。访谈过程中将录制参与者的声音。",
      "audioInterviewNotice": "⚠️ 实验性功能：这是一项新技术，准确性较低。"
    },
    "buttons": {
      "testWithSynthetic": "使用模拟人测试访谈"
    },
    "linkTypes": {
      "standardMode": "标准访谈模式",
      "standardDescription": "支持4种模式（语音对语音、语音转文本、文本转语音、文本对文本）。推荐使用。",
      "realtimeMode": "实时语音模式（实验性）",
      "realtimeDescription": "基于语音的访谈，与AI实时对话。新技术。"
    },
    "realtimeSettings": {
      "title": "实时语音设置",
      "description": "自定义实时语音访谈。默认值与旧版本保持一致。",
      "voiceLabel": "AI 语音",
      "voiceDefaultTag": "默认",
      "voiceNaturalTag": "更自然",
      "pacingLabel": "回复节奏",
      "pacingQuick": "快速 — 反应迅速",
      "pacingStandard": "标准（默认）",
      "pacingPatient": "耐心 — 等待更久",
      "pacingHelp": "受访者停止说话后，AI 等待多久再作出回复。",
      "allowInterruptionLabel": "允许受访者打断 AI",
      "allowInterruptionHelp": "开启：受访者可以打断 AI 说话。关闭：AI 说完后再开始聆听 — 可避免被笔记本电脑扬声器的回声打断。",
      "save": "保存实时设置",
      "saved": "实时设置已保存",
      "saveFailed": "实时设置保存失败"
    },
    "currentSettings": "当前设置：{{type}}，语言：{{language}}",
    "collaboration": {
      "title": "团队协作",
      "invite": "邀请协作者",
      "inviteLabel": "电子邮箱",
      "inviteButton": "发送邀请",
      "inviting": "发送中...",
      "inviteSuccess": "邀请已发送！",
      "inviteError": "发送邀请失败，请重试。",
      "alreadyInvited": "该邮箱已被邀请。",
      "collaborators": "协作者",
      "noCollaborators": "暂无协作者。邀请他人开始合作。",
      "statusPending": "待接受",
      "statusActive": "已加入",
      "remove": "移除",
      "removeConfirm": "确认移除此协作者？",
      "sharedBy": "由 {{owner}} 共享",
      "surveyHint": "邀请他人加入此项目。协作者可以查看和编辑其所有问卷。",
      "close": "关闭"
    }
  },
  fr: {
    "tutorial": {
      "title": "Pour commencer",
      "step1Title": "Configurez votre intervieweur IA",
      "step1Body": "C'est ici que vous configurez l'IA qui interroge vos participants — ses questions, sa persona et la façon dont elle relance.",
      "step2Title": "Choisissez la voix ou le texte et la langue",
      "step2Body": "Choisissez comment les participants répondent — texte, voix vers texte ou voix en temps réel — et définissez la voix de l'IA ainsi que la langue de l'entretien.",
      "step3Title": "Copiez et partagez votre lien",
      "step3Body": "Partagez le lien de votre entretien avec les participants depuis la section Liens de partage. L'IA mène chaque entretien et le transcrit automatiquement.",
      "step4Title": "Suivez l'arrivée des résultats",
      "step4Body": "Au fur et à mesure que les participants terminent, ouvrez les Analyses pour examiner les transcriptions et les thèmes — ou envoyez-les vers l'Espace de travail QDA pour un codage plus approfondi.",
      "back": "Retour",
      "next": "Suivant",
      "done": "Commencer",
      "skip": "Passer",
      "help": "Comment ça marche"
    },
    "loading": "Chargement...",
    "sections": {
      "overview": "Vue d'ensemble du projet",
      "outline": "Plan d'entretien",
      "participants": "Participants",
      "data": "Données d'entretien",
      "settings": "Paramètres du projet",
      "title": "Configuration de l'API",
      "recordingOptions": "Options d'enregistrement",
      "interviewSettings": "Paramètres d'Entretien",
      "generalSettings": "Paramètres généraux",
      "standardModeSettings": "Paramètres du mode standard",
      "outlineHelp": "Besoin d'aide pour rédiger votre plan ? VOIR LE GUIDE D'ENTRETIEN",
      "customForm": "Formulaire d'entretien personnalisé",
      "projectSettings": "Paramètres du Projet",
      "contentConfiguration": "Configuration du Contenu", 
      "dataAnalytics": "Données et Analyses",
      "customFormDescription": "Créez un formulaire personnalisé pour collecter des informations supplémentaires auprès des interviewés avant le début de l'entretien.",
      "shareLinks": "Partager les liens d'entretien",
      "navigation": "Navigation",
      "digitalTwinConsent": "Collecter le consentement pour le jumeau numérique",
      "digitalTwinConsentDescription": "Lorsqu'activé, les participants seront invités à consentir à l'utilisation de leurs données d'entretien pour créer un personnage IA (jumeau numérique) pour de futures recherches."
    },
    "formBuilder": {
      "title": "Constructeur de Formulaire",
      "previewForm": "Aperçu du Formulaire",
      "saveForm": "Enregistrer le Formulaire",
      "saving": "Enregistrement...",
      "formFields": "Champs du Formulaire",
      "noFieldsTitle": "Aucun champ personnalisé ajouté",
      "noFieldsDesc1": "Laissez vide pour utiliser le formulaire par défaut (nom, âge, genre, profession, éducation, contact)",
      "noFieldsDesc2": "Ou cliquez sur n'importe quel type de champ ci-dessus pour créer un formulaire d'entretien personnalisé",
      "addFirstField": "Ajouter Votre Premier Champ",
      "categories": {
        "input": "Champs de Saisie",
        "choice": "Champs de Choix",
        "display": "Éléments d'Affichage"
      },
      "fieldTypes": {
        "text": "Saisie de Texte",
        "textarea": "Texte Long", 
        "email": "E-mail",
        "phone": "Téléphone",
        "number": "Nombre",
        "date": "Date",
        "file": "Téléchargement de Fichier",
        "select": "Menu Déroulant",
        "radio": "Boutons Radio",
        "checkbox": "Cases à Cocher",
        "display_text": "Bloc de Texte",
        "display_image": "Image",
        "display_video": "Vidéo",
        "section_header": "En-tête de Section",
        "page_break": "Saut de Page"
      },
      "fieldDescriptions": {
        "text": "Saisie de texte sur une ligne",
        "textarea": "Zone de texte multi-lignes",
        "email": "Saisie d'adresse e-mail",
        "phone": "Saisie de numéro de téléphone", 
        "number": "Saisie numérique",
        "date": "Sélecteur de date",
        "file": "Champ de téléchargement de fichier",
        "select": "Sélection déroulante",
        "radio": "Choix unique parmi les options",
        "checkbox": "Options à choix multiples",
        "display_text": "Afficher du texte ou HTML",
        "display_image": "Afficher une image",
        "display_video": "Intégrer du contenu vidéo",
        "section_header": "Titre de section",
        "page_break": "Commencer une nouvelle page"
      },
      "fieldEditor": {
        "title": "Propriétés du Champ",
        "fieldLabel": "Libellé du Champ",
        "fieldType": "Type de Champ",
        "placeholderText": "Texte d'Indication",
        "placeholderHelp": "Le texte d'indication s'affiche dans le champ de saisie comme aide pour les utilisateurs. Il disparaît lorsqu'ils commencent à taper.",
        "helpText": "Texte d'Aide",
        "helpTextHelper": "Texte d'aide optionnel pour guider les utilisateurs",
        "required": "Champ Obligatoire",
        "options": "Options",
        "addOption": "Ajouter une Option",
        "removeOption": "Supprimer l'Option",
        "imageUrl": "URL de l'Image",
        "altText": "Texte Alternatif",
        "videoUrl": "URL de la Vidéo",
        "videoUrlHelper": "YouTube, Vimeo ou URL de fichier vidéo direct",
        "displayContent": "Contenu d'Affichage",
        "displayContentHelper": "Prend en charge les balises HTML de base",
        "textareaRows": "Nombre de Lignes",
        "fileAccept": "Types de Fichiers Acceptés",
        "maxFileSize": "Taille Max du Fichier (MB)"
      },
      "defaults": {
        "displayTextContent": "Votre texte d'affichage ici...",
        "sectionTitle": "Titre de Section"
      },
      "validation": {
        "emailAutoApplied": "La validation d'e-mail sera automatiquement appliquée",
        "numberAutoApplied": "La validation numérique sera automatiquement appliquée", 
        "phoneAutoApplied": "La validation du numéro de téléphone sera automatiquement appliquée"
      },
      "actions": {
        "duplicate": "Dupliquer",
        "moveUp": "Déplacer Vers le Haut", 
        "moveDown": "Déplacer Vers le Bas",
        "delete": "Supprimer",
        "close": "Fermer"
      },
      "labels": {
        "untitledField": "Champ Sans Titre",
        "required": "Obligatoire",
        "newField": "Nouveau"
      }
    },
    "interviewTypes": {
      "structured": "Entretien structuré",
      "semiStructured": "Entretien semi-structuré"
    },
    "languages": {
      "english": "Anglais (English)",
      "chinese": "Chinois (Chinese)",
      "french": "Français (French)",
      "norwegian": "Norvégien (Norsk)",
      "dutch": "Néerlandais (Dutch)",
      "german": "Allemand (Deutsch)",
      "spanish": "Espagnol (Español)",
      "portuguese": "Portugais (Português)",
      "japanese": "Japonais (日本語)",
      "arabic": "Arabe (العربية)",
      "flexible": "Flexible",
      "flexibleHelp": "Laissez votre interviewe sélectionner sa langue préférée"
    },
    "fields": {
      "outlineTitle": "Plan d'entretien",
      "apiConfig": "Configuration de l'API",
      "participantInfo": "Informations sur les participants",
      "recordAudio": "Enregistrer l'audio",
      "hideIntervieweeTranscript": "Masquer le texte transcrit de l'interviewé",
      "hideIntervieweeTranscriptHint": "Lorsque activé, les interviewés ne verront que les messages de l'intervieweur IA, pas leur propre discours transcrit.",
      "recordVideo": "Enregistrer la vidéo",
      "voiceEnabled": "Interviewer IA Vocale",
      "interactionMode": "Mode d'interaction",
      "aiVoice": "Voix de l'IA",
      "maxDuration": "Durée maximale de l'entretien",
      "name": "Nom",
      "age": "Âge",
      "gender": "Genre",
      "occupation": "Profession",
      "education": "Éducation",
      "interviewType": "Type d'entretien",
      "language": "Langue",
      "transcriptionEngine": "Moteur de transcription",
      "aiInterviewerName": "Nom de l'intervieweur IA",
      "aiInterviewerNamePlaceholder": "ex. Dr. Dupont, Assistant de recherche",
      "aiInterviewerNameHelper": "Laissez vide pour utiliser le nom par défaut selon la langue",
      "interviewerMode": "Mode de l'intervieweur IA",
      "interviewerModeHelp": "Choisissez combien de modèles d'IA conduisent cet entretien.",
      "probingDepth": "Profondeur de relance",
      "probingDepthHelp": "À quel point l'IA pousse les relances sur chaque réponse."
    },
    "probingDepth": {
      "lightLabel": "Léger",
      "lightDesc": "0-1 relance · priorité à la couverture · rythme rapide",
      "moderateLabel": "Modéré (recommandé)",
      "moderateDesc": "1-2 relances · équilibre profondeur/couverture",
      "deepLabel": "Approfondi",
      "deepDesc": "2-4 relances · explore motivations et exemples concrets"
    },
    "interviewerModes": {
      "singleLabel": "Modèle unique (plus rapide)",
      "singleDesc": "Une seule IA mène tout l'entretien. Latence et coût réduits.",
      "dualLabel": "Double modèle (recommandé)",
      "dualDesc": "Un modèle superviseur audite le répondeur pour des relances de meilleure qualité.",
      "structuredNotice": "Les entretiens structurés utilisent toujours le système à double modèle."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (recommandé, meilleure précision)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — meilleure précision multilingue sur 99 langues. Repli automatique vers Google → Whisper → Xunfei si nécessaire.",
      "autoSelectedElevenLabs": "✓ ElevenLabs sélectionné automatiquement",
      "voicePicker": {
        "autoLabel": "Auto (recommandé)",
        "autoDesc": "ElevenLabs Bella · voix féminine multilingue · 10 langues",
        "warmFemaleMultilingual": "Féminine chaleureuse · multilingue",
        "calmFemaleMultilingual": "Féminine posée · multilingue",
        "youngFemaleMultilingual": "Féminine jeune · multilingue",
        "matureMaleMultilingual": "Masculine mature · multilingue",
        "warmMaleMultilingual": "Masculine chaleureuse · multilingue",
        "casualMaleMultilingual": "Masculine décontractée · multilingue",
        "mimoDesc": "Voix féminine chinoise native · idéale pour le mandarin",
        "openaiDesc": "Voix neutre · multilingue",
        "xunfeiDesc": "Féminine chinoise (legacy)",
        "recommendedBadge": "RECOMMANDÉ",
        "orPickSpecific": "Ou choisir une voix spécifique :",
        "filterAll": "Toutes les voix",
        "filterFemale": "Féminine",
        "filterMale": "Masculine",
        "filterChinese": "Meilleure pour le chinois",
        "noMatch": "Aucune voix ne correspond à ce filtre."
      },
      "google": "Google Speech (recommandé, meilleure précision)",
      "whisper": "Whisper (rapide, bonne précision)",
      "xunfei": "Xunfei (lent, plus recommandé)",
      "helperChinese": "Google Speech (recommandé) — meilleure précision sur le mandarin. Whisper est plus rapide et moins coûteux. Xunfei est conservé en repli uniquement.",
      "helperDefault": "Whisper (recommandé) — rapide et économique. Google Speech offre une meilleure précision à un coût plus élevé.",
      "autoSelectedXunfei": "✓ Xunfei sélectionné automatiquement pour le chinois",
      "autoSelectedGoogle": "✓ Google sélectionné automatiquement pour le chinois",
      "autoSelectedWhisper": "✓ Whisper sélectionné automatiquement"
    },
    "interactionModes": {
      "textToText": "Texte à texte",
      "textToVoice": "Texte à voix + texte",
      "voiceToText": "Voix à texte",
      "voiceToVoice": "Voix à voix + texte",
      "textToTextDesc": "Tapez vos réponses, lisez les réponses IA (Pas d'audio)",
      "textToVoiceDesc": "Tapez vos réponses, l'IA répond avec voix + texte",
      "voiceToTextDesc": "Parlez vos réponses, lisez les réponses IA (Pas d'audio)",
      "voiceToVoiceDesc": "Parlez vos réponses, l'IA répond avec voix + texte"
    },
    "actions": {
      "saveOutline": "Enregistrer le plan",
      "updateApiKey": "Mettre à jour la clé API",
      "downloadData": "Télécharger les données de l'entretien",
      "liveSupervision": "Supervision en direct",
      "downloadCsv": "Télécharger le CSV",
      "deleteParticipant": "Supprimer le participant",
      "addKey": "Ajouter une clé API",
      "saveRecordingPreferences": "Enregistrer les préférences d'enregistrement",
      "saveInterviewSettings": "Enregistrer les paramètres de l'entretien",
      "saveGeneralSettings": "Enregistrer les paramètres généraux",
      "saveStandardModeSettings": "Enregistrer les paramètres du mode standard",
      "viewInterviewGuide": "Voir le guide d'entretien",
      "editProjectName": "Modifier le nom du projet",
      "findParticipants": "Trouver des Participants",
      "testWithSynthetic": "Tester avec un humain synthétique",
      "classicView": "Vue Classique",
      "newView": "Nouvelle Mise en Page"
    },
    "messages": {
      "outlineSaved": "Plan enregistré avec succès",
      "outlineSaveFailed": "Échec de l'enregistrement du plan",
      "saveFailed": "Échec de l'enregistrement du plan",
      "apiKeyUpdated": "Clé API mise à jour avec succès",
      "apiKeyError": "Échec de la mise à jour de la clé API",
      "downloadStarted": "Téléchargement commencé",
      "downloadComplete": "Téléchargement terminé",
      "downloadFailed": "Échec du téléchargement",
      "noParticipants": "Aucun participant trouvé",
      "deleteConfirm": "Êtes-vous sûr de vouloir supprimer ce participant ?",
      "deleteSuccess": "Participant supprimé avec succès",
      "deleteFailed": "Échec de la suppression du participant",
      "notFound": "Projet non trouvé",
      "noKey": "Clé API non configurée",
      "settingsSaved": "Paramètres de l'entretien enregistrés avec succès",
      "settingsScopeNote": "Ces paramètres s'appliquent uniquement au lien d'entretien en mode Standard (Standard Mode). Le lien Audio en temps réel (Realtime Audio) (expérimental) possède ses propres réglages de voix, de rythme des réponses et d'interruption (dans le panneau Mode vocal en temps réel ci-dessus) ; le mode d'interaction, le moteur de transcription, le mode d'intervieweur, la profondeur des relances et le nom de l'intervieweur IA s'appliquent uniquement au mode Standard (Standard Mode).",
      "generalSettingsScopeNote": "Ces paramètres s'appliquent à la fois au lien d'entretien en mode standard et au lien audio en temps réel (expérimental).",
      "settingsError": "Échec de l'enregistrement des paramètres de l'entretien",
      "loadError": "Impossible de charger les détails du projet, veuillez réessayer plus tard",
      "timeoutError": "Délai d'attente de la requête - veuillez vérifier votre connexion",
      "fetchError": "Échec du chargement des détails du projet",
      "participantsTimeout": "Échec du chargement des données des participants en raison d'un délai d'attente, veuillez vérifier votre connexion",
      "participantsError": "Échec de la récupération des participants",
      "participantsPagination": "Total {{total}} participants, page {{page}} sur {{pages}}",
      "dataVisualization": "Exploration des Données",
      "themeAnalysisLens": "Analyse thématique avec ThemeLens",
      "inductiveCoding": "Codage Inductif IA",
      "optimizeOutline": "Optimiser le Plan IA",
      "optimizationResult": "Résultat de l'Optimisation",
      "optimizedOutline": "Plan Optimisé :",
      "improvements": "Améliorations Clés :",
      "suggestions": "Suggestions :",
      "applyOptimization": "Appliquer l'Optimisation",
      "startOptimization": "Commencer l'Optimisation",
      "optimizationGoals": "Objectifs d'Optimisation (Optionnel)",
      "optimizationGoalsPlaceholder": "par ex. : Augmenter la profondeur des questions, améliorer l'ouverture des questions, optimiser la logique des questions, etc.",
      "unsavedChanges": "Vous avez des modifications non sauvegardées",
      "changesDiscarded": "Modifications annulées",
      "optimizing": "Optimisation en cours...",
      "recordingPreferencesSaved": "Préférences d'enregistrement sauvegardées",
      "recordingPreferencesSaveFailed": "Échec de la sauvegarde des préférences d'enregistrement",
      "outlineRequired": "Le plan est requis",
      "outlineOptimized": "Plan optimisé",
      "outlineOptimizeFailed": "Échec de l'optimisation du plan",
      "optimizedOutlineApplied": "Plan optimisé appliqué",
      "projectNameSaved": "Nom du projet enregistré avec succès",
      "projectNameSaveFailed": "Échec de l'enregistrement du nom du projet",
      "unsavedProjectNameChanges": "Vous avez des modifications non sauvegardées du nom du projet",
      "shareLinkInfo": "Partagez ces liens avec vos interviewés. Ils peuvent accéder à l'entretien directement sans connexion.",
      "standardLinkCopied": "Lien mode standard copié dans le presse-papiers",
      "realtimeLinkCopied": "Lien mode temps réel copié dans le presse-papiers",
      "selectInterviewFirst": "Veuillez d'abord sélectionner un entretien."
    },
    "downloads": {
      "title": "Options de téléchargement",
      "description": "Choisissez ce que vous voulez télécharger :",
      "downloadEverything": "Tout télécharger",
      "downloadMetadataOnly": "Télécharger uniquement les métadonnées",
      "downloadAudioForSpecificInterview": "Télécharger l'audio d'un entretien spécifique",
      "interviewLabel": "Entretien",
      "downloadSelectedInterviewAudio": "Télécharger l'audio de l'entretien sélectionné"
    },
    "warnings": {
      "audioRecordingEnabled": "Avertissement : L'enregistrement audio est activé pour ce projet. Les voix des participants seront enregistrées pendant les entretiens.",
      "audioInterviewNotice": "⚠️ Fonctionnalité expérimentale : Il s'agit d'une nouvelle technologie avec une précision moindre."
    },
    "buttons": {
      "testWithSynthetic": "Tester l'entretien avec un humain synthétique"
    },
    "linkTypes": {
      "standardMode": "Mode d'entretien standard",
      "standardDescription": "Prend en charge 4 modalités (voix à voix, voix à texte, texte à voix, texte à texte). Recommandé.",
      "realtimeMode": "Mode vocal en temps réel (Expérimental)",
      "realtimeDescription": "Entretien vocal avec conversation IA en temps réel. Nouvelle technologie."
    },
    "realtimeSettings": {
      "title": "Paramètres vocaux en temps réel",
      "description": "Personnalisez l'entretien audio en temps réel. Les valeurs par défaut correspondent à la version précédente.",
      "voiceLabel": "Voix de l'IA",
      "voiceDefaultTag": "par défaut",
      "voiceNaturalTag": "plus naturelle",
      "pacingLabel": "Rythme des réponses",
      "pacingQuick": "Rapide — répond vite",
      "pacingStandard": "Standard (par défaut)",
      "pacingPatient": "Patient — attend plus longtemps",
      "pacingHelp": "Durée pendant laquelle l'IA attend après que le participant a cessé de parler avant de répondre.",
      "allowInterruptionLabel": "Autoriser les participants à interrompre l'IA",
      "allowInterruptionHelp": "Activé : les participants peuvent parler en même temps que l'IA. Désactivé : l'IA termine de parler avant d'écouter — cela évite qu'elle soit interrompue par l'écho des haut-parleurs de l'ordinateur portable.",
      "save": "Enregistrer les paramètres en temps réel",
      "saved": "Paramètres en temps réel enregistrés",
      "saveFailed": "Échec de l'enregistrement des paramètres en temps réel"
    },
    "currentSettings": "Paramètres actuels : {{type}}, Langue : {{language}}",
    "collaboration": {
      "title": "Équipe",
      "invite": "Inviter un collaborateur",
      "inviteLabel": "Adresse e-mail",
      "inviteButton": "Inviter",
      "inviting": "Envoi en cours...",
      "inviteSuccess": "Invitation envoyée !",
      "inviteError": "Échec de l'envoi de l'invitation. Veuillez réessayer.",
      "alreadyInvited": "Cet e-mail a déjà été invité.",
      "collaborators": "Collaborateurs",
      "noCollaborators": "Aucun collaborateur pour l'instant. Invitez quelqu'un pour commencer.",
      "statusPending": "En attente",
      "statusActive": "Actif",
      "remove": "Retirer",
      "removeConfirm": "Retirer ce collaborateur ?",
      "sharedBy": "Partagé par {{owner}}",
      "surveyHint": "Invitez des personnes à ce projet. Les collaborateurs peuvent voir et modifier tous ses questionnaires.",
      "close": "Fermer"
    }
  },
  nb: {
    "tutorial": {
      "title": "Kom i gang",
      "step1Title": "Sett opp AI-intervjueren din",
      "step1Body": "Her konfigurerer du AI-en som intervjuer deltakerne dine — spørsmålene, personligheten og hvordan den følger opp.",
      "step2Title": "Velg tale eller tekst og språk",
      "step2Body": "Bestem hvordan deltakerne svarer — tekst, tale-til-tekst eller sanntidstale — og angi AI-stemmen og intervjuspråket.",
      "step3Title": "Kopier og del lenken din",
      "step3Body": "Del intervjulenken med deltakerne fra Delingslenker-seksjonen. AI-en gjennomfører hvert intervju og transkriberer det automatisk.",
      "step4Title": "Se resultatene komme inn",
      "step4Body": "Etter hvert som deltakerne fullfører, åpne Analyse for å gjennomgå transkripsjoner og temaer — eller send dem til QDA-arbeidsområdet for dypere koding.",
      "back": "Tilbake",
      "next": "Neste",
      "done": "Kom i gang",
      "skip": "Hopp over",
      "help": "Slik fungerer det"
    },
    "loading": "Laster...",
    "sections": {
        "overview": "Prosjektoversikt",
        "outline": "Intervjuplan",
        "participants": "Deltakere",
        "data": "Intervjudata",
        "settings": "Prosjektinnstillinger",
        "title": "API-konfigurasjon",
        "recordingOptions": "Opptaksalternativer",
        "interviewSettings": "Intervjuinnstillinger",
        "generalSettings": "Generelle innstillinger",
        "standardModeSettings": "Innstillinger for standardmodus",
        "outlineHelp": "Trenger du hjelp til å skrive disposisjonen? SE INTERVJUGUIDEN",
        "customForm": "Tilpasset intervjuskjema",
        "projectSettings": "Prosjektinnstillinger",
        "contentConfiguration": "Innholdskonfigurasjon", 
        "dataAnalytics": "Data og Analyse",
        "customFormDescription": "Opprett et tilpasset skjema for å samle inn tilleggsinformasjon fra intervjuobjekter før intervjuet begynner.",
        "shareLinks": "Del intervjulenker",
        "navigation": "Navigasjon",
        "digitalTwinConsent": "Samle inn samtykke for digital tvilling",
        "digitalTwinConsentDescription": "Når aktivert, vil deltakerne bli spurt om de samtykker til at intervjudataene deres brukes til å opprette en AI-persona (digital tvilling) for fremtidig forskning."
    },
    "formBuilder": {
      "title": "Skjemabygger",
      "previewForm": "Forhåndsvis Skjema",
      "saveForm": "Lagre Skjema",
      "saving": "Lagrer...",
      "formFields": "Skjemafelt",
      "noFieldsTitle": "Ingen tilpassede felt lagt til ennå",
      "noFieldsDesc1": "La dette være tomt for å bruke standardskjemaet (navn, alder, kjønn, yrke, utdanning, kontakt)",
      "noFieldsDesc2": "Eller klikk på en felttype ovenfor for å opprette et tilpasset intervjuskjema",
      "addFirstField": "Legg til Ditt Første Felt",
      "categories": {
        "input": "Inndatafelt",
        "choice": "Valgfelt",
        "display": "Visningselementer"
      },
      "fieldTypes": {
        "text": "Tekstinndata",
        "textarea": "Lang Tekst", 
        "email": "E-post",
        "phone": "Telefon",
        "number": "Nummer",
        "date": "Dato",
        "file": "Filopplasting",
        "select": "Rullegardin",
        "radio": "Radioknapper",
        "checkbox": "Avkrysningsbokser",
        "display_text": "Tekstblokk",
        "display_image": "Bilde",
        "display_video": "Video",
        "section_header": "Seksjonsoverskrift",
        "page_break": "Sideskift"
      },
      "fieldDescriptions": {
        "text": "Enkeltlinje tekstinndata",
        "textarea": "Flerlinje tekstområde",
        "email": "E-postadresse inndata",
        "phone": "Telefonnummer inndata", 
        "number": "Numerisk inndata",
        "date": "Datovalger",
        "file": "Filopplastingsfelt",
        "select": "Rullgardinvalg",
        "radio": "Enkeltvalg fra alternativer",
        "checkbox": "Flervalgsalternativer",
        "display_text": "Vis tekst eller HTML",
        "display_image": "Vis et bilde",
        "display_video": "Bygg inn videoinnhold",
        "section_header": "Seksjonstittel",
        "page_break": "Start ny side"
      },
      "fieldEditor": {
        "title": "Feltegenskaper",
        "fieldLabel": "Feltetikett",
        "fieldType": "Felttype",
        "placeholderText": "Plassholdertekst",
        "placeholderHelp": "Plassholdertekst vises inne i inndatafeltet som en veiledning for brukere. Den forsvinner når de begynner å skrive.",
        "helpText": "Hjelpetekst",
        "helpTextHelper": "Valgfri hjelpetekst for å veilede brukere",
        "required": "Obligatorisk Felt",
        "options": "Alternativer",
        "addOption": "Legg til Alternativ",
        "removeOption": "Fjern Alternativ",
        "imageUrl": "Bilde-URL",
        "altText": "Alt-tekst",
        "videoUrl": "Video-URL",
        "videoUrlHelper": "YouTube, Vimeo eller direkte video-fil URL",
        "displayContent": "Visningsinnhold",
        "displayContentHelper": "Støtter grunnleggende HTML-tagger",
        "textareaRows": "Antall Rader",
        "fileAccept": "Godkjente Filtyper",
        "maxFileSize": "Maks Filstørrelse (MB)"
      },
      "defaults": {
        "displayTextContent": "Din visningstekst her...",
        "sectionTitle": "Seksjonstitel"
      },
      "validation": {
        "emailAutoApplied": "E-postvalidering vil bli automatisk anvendt",
        "numberAutoApplied": "Tallvalidering vil bli automatisk anvendt", 
        "phoneAutoApplied": "Telefonnummervalidering vil bli automatisk anvendt"
      },
      "actions": {
        "duplicate": "Dupliser",
        "moveUp": "Flytt Opp", 
        "moveDown": "Flytt Ned",
        "delete": "Slett",
        "close": "Lukk"
      },
      "labels": {
        "untitledField": "Felt Uten Tittel",
        "required": "Obligatorisk",
        "newField": "Ny"
      }
    },
    "interviewTypes": {
        "structured": "Strukturert intervju",
        "semiStructured": "Semi-strukturert intervju"
    },
    "languages": {
        "english": "Engelsk (English)",
        "chinese": "Kinesisk (Chinese)",
        "french": "Fransk (Français)",
        "norwegian": "Norsk (Norwegian)",
        "dutch": "Nederlandsk (Dutch)",
        "german": "Tysk (Deutsch)",
        "spanish": "Spansk (Español)",
        "portuguese": "Portugisisk (Português)",
        "japanese": "Japansk (日本語)",
        "arabic": "Arabisk (العربية)",
        "flexible": "Fleksibel",
        "flexibleHelp": "La intervjuobjektet velge sitt foretrukne språk"
    },
    "currentSettings": "Nåværende innstillinger: {{type}}, Språk: {{language}}",    "fields": {
        "outlineTitle": "Intervjuplan",
        "apiConfig": "API-konfigurasjon",
        "participantInfo": "Deltakerinformasjon",
        "recordAudio": "Ta opp lyd",
        "hideIntervieweeTranscript": "Skjul intervjuobjektets transkriberte tekst",
        "hideIntervieweeTranscriptHint": "Når aktivert, vil intervjuobjektet bare se meldinger fra AI-intervjueren, ikke sin egen transkriberte tale.",
        "recordVideo": "Ta opp video",
        "voiceEnabled": "AI Stemme-intervjuer",
        "interactionMode": "Interaksjonsmodus",
        "aiVoice": "KI-stemme",
        "maxDuration": "Maksimal intervjuvarighet",
        "name": "Navn",
        "age": "Alder",
        "gender": "Kjønn",
        "occupation": "Yrke",
        "education": "Utdanning",
        "interviewType": "Intervjutype",
        "language": "Språk",
        "transcriptionEngine": "Transkripsjonsmotor",
        "aiInterviewerName": "Navn på AI-intervjuer",
        "aiInterviewerNamePlaceholder": "f.eks. Dr. Hansen, Forskningsassistent",
        "aiInterviewerNameHelper": "La stå tom for å bruke standardnavnet basert på intervjuspråk",
        "interviewerMode": "AI-intervjuermodus",
        "interviewerModeHelp": "Velg hvor mange AI-modeller som skal drive dette intervjuet.",
        "probingDepth": "Oppfølgingsdybde",
        "probingDepthHelp": "Hvor sterkt AI-en følger opp på hvert svar."
    },
    "probingDepth": {
      "lightLabel": "Lett",
      "lightDesc": "0-1 oppfølginger · prioriterer dekning · raskt tempo",
      "moderateLabel": "Moderat (anbefalt)",
      "moderateDesc": "1-2 oppfølginger · balansert dybde og dekning",
      "deepLabel": "Dyp",
      "deepDesc": "2-4 oppfølginger · graver i motiver og eksempler"
    },
    "interviewerModes": {
      "singleLabel": "Én modell (raskere)",
      "singleDesc": "Én AI håndterer hele intervjuet. Lavere ventetid og kostnad.",
      "dualLabel": "Dobbel modell (anbefalt)",
      "dualDesc": "En veiledermodell kontrollerer respondentmodellen for bedre oppfølgingsspørsmål.",
      "structuredNotice": "Strukturerte intervjuer bruker alltid dobbeltmodell-systemet."
    },
    "transcriptionEngine": {
        "elevenlabs": "ElevenLabs Scribe v2 (anbefalt, best nøyaktighet)",
        "helperWithElevenLabs": "ElevenLabs Scribe v2 — best flerspråklig nøyaktighet på 99 språk. Faller automatisk tilbake til Google → Whisper → Xunfei ved behov.",
        "autoSelectedElevenLabs": "✓ ElevenLabs automatisk valgt",
        "voicePicker": {
            "autoLabel": "Auto (anbefalt)",
            "autoDesc": "ElevenLabs Bella · flerspråklig kvinnestemme · alle 10 språk",
            "warmFemaleMultilingual": "Varm kvinne · flerspråklig",
            "calmFemaleMultilingual": "Rolig kvinne · flerspråklig",
            "youngFemaleMultilingual": "Ung kvinne · flerspråklig",
            "matureMaleMultilingual": "Moden mann · flerspråklig",
            "warmMaleMultilingual": "Varm mann · flerspråklig",
            "casualMaleMultilingual": "Avslappet mann · flerspråklig",
            "mimoDesc": "Innfødt kinesisk kvinne · best for mandarin",
            "openaiDesc": "Nøytral stemme · flerspråklig",
            "xunfeiDesc": "Kinesisk kvinne (eldre)",
            "recommendedBadge": "ANBEFALT",
            "orPickSpecific": "Eller velg en bestemt stemme:",
            "filterAll": "Alle stemmer",
            "filterFemale": "Kvinnelig",
            "filterMale": "Mannlig",
            "filterChinese": "Best for kinesisk",
            "noMatch": "Ingen stemmer matcher dette filteret."
        },
        "google": "Google Speech (anbefalt, best nøyaktighet)",
        "whisper": "Whisper (rask, god nøyaktighet)",
        "xunfei": "Xunfei (treg, anbefales ikke lenger)",
        "helperChinese": "Google Speech (anbefalt) — best nøyaktighet på mandarin. Whisper er raskere og rimeligere. Xunfei beholdes kun som reservevalg.",
        "helperDefault": "Whisper (anbefalt) — rask og kostnadseffektiv. Google Speech gir høyere nøyaktighet, men koster mer.",
        "autoSelectedXunfei": "✓ Xunfei automatisk valgt for kinesisk",
        "autoSelectedGoogle": "✓ Google automatisk valgt for kinesisk",
        "autoSelectedWhisper": "✓ Whisper automatisk valgt"
    },
    "interactionModes": {
      "textToText": "Tekst til tekst",
      "textToVoice": "Tekst til stemme + tekst",
      "voiceToText": "Stemme til tekst",
      "voiceToVoice": "Stemme til stemme + tekst",
      "textToTextDesc": "Skriv dine svar, les AI-svar (Ingen lyd)",
      "textToVoiceDesc": "Skriv dine svar, AI svarer med stemme + tekst",
      "voiceToTextDesc": "Snakk dine svar, les AI-svar (Ingen lyd)",
      "voiceToVoiceDesc": "Snakk dine svar, AI svarer med stemme + tekst"
    },
    "actions": {
        "saveOutline": "Lagre plan",
        "updateApiKey": "Oppdater API-nøkkel",
        "downloadData": "Last ned intervjudata",
        "liveSupervision": "Direkte tilsyn",
        "downloadCsv": "Last ned CSV",
        "deleteParticipant": "Slett deltaker",
        "addKey": "Legg til API-nøkkel",
        "saveRecordingPreferences": "Lagre opptaksinnstillinger",
        "saveInterviewSettings": "Lagre innstillinger",
        "saveGeneralSettings": "Lagre generelle innstillinger",
        "saveStandardModeSettings": "Lagre standardmodus-innstillinger",
        "viewInterviewGuide": "Se intervjuguiden",
      "editProjectName": "Rediger prosjektnavn",
      "findParticipants": "Finn Deltakere",
      "testWithSynthetic": "Test med syntetisk menneske",
      "classicView": "Klassisk Visning",
      "newView": "Nytt Oppsett"
    },
    "messages": {
        "outlineSaved": "Plan lagret",
        "outlineSaveFailed": "Kunne ikke lagre plan",
        "saveFailed": "Kunne ikke lagre plan",
        "apiKeyUpdated": "API-nøkkel oppdatert",
        "apiKeyError": "Kunne ikke oppdatere API-nøkkel",
        "downloadStarted": "Nedlasting startet",
        "downloadComplete": "Nedlasting fullført",
        "downloadFailed": "Nedlasting mislyktes",
        "noParticipants": "Ingen deltakere funnet",
        "deleteConfirm": "Er du sikker på at du vil slette denne deltakeren?",
        "deleteSuccess": "Deltaker slettet",
        "deleteFailed": "Kunne ikke slette deltaker",
        "notFound": "Prosjekt ikke funnet",
        "noKey": "Ingen API-nøkkel konfigurert",
        "settingsSaved": "Intervjuinnstillinger lagret",
        "settingsScopeNote": "Disse innstillingene gjelder kun for intervjulenken i Standard Mode. Realtime Audio-lenken (eksperimentell) har sine egne innstillinger for stemme, svartempo og avbrytelse (i panelet Sanntids stemmemodus ovenfor); samhandlingsmodus, transkripsjonsmotor, intervjuermodus, sonderingsdybde og navn på AI-intervjuer gjelder kun for Standard Mode.",
        "generalSettingsScopeNote": "Disse innstillingene gjelder både standardmodus-intervjulenken og sanntidslyd-lenken (eksperimentell).",
        "settingsError": "Kunne ikke lagre intervjuinnstillinger",
        "loadError": "Kunne ikke laste prosjektdetaljer, vennligst prøv igjen senere",
        "timeoutError": "Forespørsel tidsavbrudd - vennligst sjekk tilkoblingen din",
        "fetchError": "Kunne ikke laste prosjektdetaljer",
        "participantsTimeout": "Kunne ikke laste deltakersdata på grunn av tidsavbrudd, vennligst sjekk tilkoblingen din",
        "participantsError": "Kunne ikke hente deltakere",
        "participantsPagination": "Totalt {{total}} deltakere, side {{page}} av {{pages}}",
        "dataVisualization": "Datautforskning",
        "themeAnalysisLens": "Temaanalyse med ThemeLens",
        "inductiveCoding": "AI Induktiv Koding",
        "optimizeOutline": "AI Optimaliser Disposisjon",
        "optimizationResult": "Optimaliseringsresultat",
        "optimizedOutline": "Optimalisert Disposisjon:",
        "improvements": "Hovedforbedringer:",
        "suggestions": "Forslag:",
        "applyOptimization": "Bruk Optimalisering",
        "startOptimization": "Start Optimalisering",
        "optimizationGoals": "Optimaliseringsmål (Valgfritt)",
        "optimizationGoalsPlaceholder": "f.eks.: Øk spørsmålsdybde, forbedre spørsmålsåpenhet, optimaliser spørsmålslogikk, etc.",
        "unsavedChanges": "Du har ulagrede endringer",
        "changesDiscarded": "Endringer forkastet",
        "optimizing": "Optimaliserer...",
        "recordingPreferencesSaved": "Opptaksinnstillinger lagret",
        "recordingPreferencesSaveFailed": "Kunne ikke lagre opptaksinnstillinger",
        "outlineRequired": "Disposisjon er påkrevd",
        "outlineOptimized": "Disposisjon optimalisert",
        "outlineOptimizeFailed": "Disposisjonsoptimalisering mislyktes",
        "optimizedOutlineApplied": "Optimalisert disposisjon anvendt",
        "projectNameSaved": "Prosjektnavn lagret",
        "projectNameSaveFailed": "Kunne ikke lagre prosjektnavn",
        "unsavedProjectNameChanges": "Du har ulagrede endringer i prosjektnavnet",
        "shareLinkInfo": "Del disse lenkene med dine intervjuobjekter. De kan få tilgang til intervjuet direkte uten innlogging.",
        "standardLinkCopied": "Standardmodus-lenke kopiert til utklippstavlen",
      "realtimeLinkCopied": "Sanntidsmodus-lenke kopiert til utklippstavlen",
      "selectInterviewFirst": "Vennligst velg et intervju først."
    },
    "downloads": {
      "title": "Nedlastingsalternativer",
      "description": "Velg hva du vil laste ned:",
      "downloadEverything": "Last ned alt",
      "downloadMetadataOnly": "Last ned kun metadata",
      "downloadAudioForSpecificInterview": "Last ned lyd for et spesifikt intervju",
      "interviewLabel": "Intervju",
      "downloadSelectedInterviewAudio": "Last ned lyd for valgt intervju"
    },
    "warnings": {
        "audioRecordingEnabled": "Advarsel: Lydopptak er aktivert for dette prosjektet. Deltakernes stemmer vil bli tatt opp under intervjuer.",
        "audioInterviewNotice": "⚠️ Eksperimentell funksjon: Dette er ny teknologi med lavere nøyaktighet."
    },
    "buttons": {
      "testWithSynthetic": "Test intervju med syntetisk menneske"
    },
    "linkTypes": {
      "standardMode": "Standard Intervjumodus",
      "standardDescription": "Støtter 4 modaliteter (tale-til-tale, tale-til-tekst, tekst-til-tale, tekst-til-tekst). Anbefalt.",
      "realtimeMode": "Sanntids Stemmemodus (Eksperimentell)",
      "realtimeDescription": "Stemmebasert intervju med sanntids AI-samtale. Ny teknologi."
    },
    "realtimeSettings": {
      "title": "Sanntids stemmeinnstillinger",
      "description": "Tilpass sanntids lydintervjuet. Standardverdiene samsvarer med forrige versjon.",
      "voiceLabel": "AI-stemme",
      "voiceDefaultTag": "standard",
      "voiceNaturalTag": "mer naturlig",
      "pacingLabel": "Svartempo",
      "pacingQuick": "Raskt — svarer kjapt",
      "pacingStandard": "Standard (standardvalg)",
      "pacingPatient": "Tålmodig — venter lenger",
      "pacingHelp": "Hvor lenge AI-en venter etter at deltakeren slutter å snakke før den svarer.",
      "allowInterruptionLabel": "La deltakere avbryte AI-en",
      "allowInterruptionHelp": "På: deltakere kan snakke i munnen på AI-en. Av: AI-en snakker ferdig før den lytter — hindrer at den blir avbrutt av ekko fra bærbar-høyttalere.",
      "save": "Lagre sanntidsinnstillinger",
      "saved": "Sanntidsinnstillinger lagret",
      "saveFailed": "Kunne ikke lagre sanntidsinnstillinger"
    },
    "collaboration": {
      "title": "Team",
      "invite": "Inviter samarbeidspartner",
      "inviteLabel": "E-postadresse",
      "inviteButton": "Inviter",
      "inviting": "Sender...",
      "inviteSuccess": "Invitasjon sendt!",
      "inviteError": "Kunne ikke sende invitasjon. Prøv igjen.",
      "alreadyInvited": "Denne e-posten er allerede invitert.",
      "collaborators": "Samarbeidspartnere",
      "noCollaborators": "Ingen samarbeidspartnere ennå. Inviter noen for å komme i gang.",
      "statusPending": "Venter",
      "statusActive": "Aktiv",
      "remove": "Fjern",
      "removeConfirm": "Fjerne denne samarbeidspartneren?",
      "sharedBy": "Delt av {{owner}}",
      "surveyHint": "Inviter folk til dette prosjektet. Samarbeidspartnere kan se og redigere alle undersøkelsene.",
      "close": "Lukk"
    }
  },
  nl: {
    "tutorial": {
      "title": "Aan de slag",
      "step1Title": "Stel je AI-interviewer in",
      "step1Body": "Hier configureer je de AI die je deelnemers interviewt — de vragen, de persona en hoe deze doorvraagt.",
      "step2Title": "Kies spraak of tekst & taal",
      "step2Body": "Bepaal hoe deelnemers reageren — tekst, spraak-naar-tekst of realtime spraak — en stel de AI-stem en de interviewtaal in.",
      "step3Title": "Kopieer & deel je link",
      "step3Body": "Deel je interviewlink met deelnemers via het gedeelte Links delen. De AI voert elk interview uit en transcribeert het automatisch.",
      "step4Title": "Bekijk de resultaten binnenkomen",
      "step4Body": "Zodra deelnemers klaar zijn, open je Analyse om transcripties en thema's te bekijken — of stuur je ze naar de QDA-werkruimte voor diepere codering.",
      "back": "Terug",
      "next": "Volgende",
      "done": "Aan de slag",
      "skip": "Overslaan",
      "help": "Hoe het werkt"
    },
    "loading": "Laden...",
    "sections": {
      "overview": "Projectoverzicht",
      "outline": "Interviewschema",
      "participants": "Deelnemers",
      "data": "Interviewgegevens",
      "settings": "Projectinstellingen",
      "title": "API-configuratie",
      "recordingOptions": "Opnameopties",
      "interviewSettings": "Interview-instellingen",
      "generalSettings": "Algemene instellingen",
      "standardModeSettings": "Instellingen voor standaardmodus",
      "outlineHelp": "Hulp nodig bij het schrijven van je schema? BEKIJK DE INTERVIEWGIDS",
      "customForm": "Aangepast interviewformulier",
      "projectSettings": "Projectinstellingen",
      "contentConfiguration": "Inhoudsconfiguratie", 
      "dataAnalytics": "Gegevens en Analyses",
      "customFormDescription": "Maak een aangepast formulier om aanvullende informatie van geïnterviewden te verzamelen voordat het interview begint.",
      "shareLinks": "Interview links delen",
      "navigation": "Navigatie",
      "digitalTwinConsent": "Toestemming voor digitale tweeling verzamelen",
      "digitalTwinConsentDescription": "Wanneer ingeschakeld, worden deelnemers gevraagd of zij instemmen met het gebruik van hun interviewgegevens om een AI-persona (digitale tweeling) te maken voor toekomstig onderzoek."
    },
    "formBuilder": {
      "title": "Formulierbouwer",
      "previewForm": "Formulier Voorvertoning",
      "saveForm": "Formulier Opslaan",
      "saving": "Opslaan...",
      "formFields": "Formuliervelden",
      "noFieldsTitle": "Nog geen aangepaste velden toegevoegd",
      "noFieldsDesc1": "Laat dit leeg om het standaardformulier te gebruiken (naam, leeftijd, geslacht, beroep, onderwijs, contact)",
      "noFieldsDesc2": "Of klik op een veldtype hierboven om een aangepast interviewformulier te maken",
      "addFirstField": "Voeg Uw Eerste Veld Toe",
      "categories": {
        "input": "Invoervelden",
        "choice": "Keuze Velden",
        "display": "Weergave-elementen"
      },
      "fieldTypes": {
        "text": "Tekstinvoer",
        "textarea": "Lange Tekst", 
        "email": "E-mail",
        "phone": "Telefoon",
        "number": "Nummer",
        "date": "Datum",
        "file": "Bestand Upload",
        "select": "Dropdown",
        "radio": "Radioknoppen",
        "checkbox": "Selectievakjes",
        "display_text": "Tekstblok",
        "display_image": "Afbeelding",
        "display_video": "Video",
        "section_header": "Sectiekop",
        "page_break": "Pagina-einde"
      },
      "fieldDescriptions": {
        "text": "Enkele regel tekstinvoer",
        "textarea": "Meerdere regels tekstgebied",
        "email": "E-mailadres invoer",
        "phone": "Telefoonnummer invoer", 
        "number": "Numerieke invoer",
        "date": "Datumkiezer",
        "file": "Bestandsupload veld",
        "select": "Dropdown selectie",
        "radio": "Enkele keuze uit opties",
        "checkbox": "Meervoudige keuze opties",
        "display_text": "Tekst of HTML weergeven",
        "display_image": "Een afbeelding weergeven",
        "display_video": "Video-inhoud insluiten",
        "section_header": "Sectietitel",
        "page_break": "Nieuwe pagina starten"
      },
      "fieldEditor": {
        "title": "Veldeigenschappen",
        "fieldLabel": "Veldlabel",
        "fieldType": "Veldtype",
        "placeholderText": "Plaatshouder Tekst",
        "placeholderHelp": "Plaatshouder tekst wordt getoond in het invoerveld als hint voor gebruikers. Het verdwijnt wanneer ze beginnen te typen.",
        "helpText": "Help Tekst",
        "helpTextHelper": "Optionele hulptekst om gebruikers te begeleiden",
        "required": "Verplicht Veld",
        "options": "Opties",
        "addOption": "Optie Toevoegen",
        "removeOption": "Optie Verwijderen",
        "imageUrl": "Afbeelding URL",
        "altText": "Alt Tekst",
        "videoUrl": "Video URL",
        "videoUrlHelper": "YouTube, Vimeo of directe video bestand URL",
        "displayContent": "Weergave Inhoud",
        "displayContentHelper": "Ondersteunt basis HTML-tags",
        "textareaRows": "Aantal Rijen",
        "fileAccept": "Geaccepteerde Bestandstypen",
        "maxFileSize": "Max Bestandsgrootte (MB)"
      },
      "defaults": {
        "displayTextContent": "Uw weergavetekst hier...",
        "sectionTitle": "Sectietitel"
      },
      "validation": {
        "emailAutoApplied": "E-mailvalidatie wordt automatisch toegepast",
        "numberAutoApplied": "Nummervalidatie wordt automatisch toegepast", 
        "phoneAutoApplied": "Telefoonnummervalidatie wordt automatisch toegepast"
      },
      "actions": {
        "duplicate": "Dupliceren",
        "moveUp": "Omhoog Verplaatsen", 
        "moveDown": "Omlaag Verplaatsen",
        "delete": "Verwijderen",
        "close": "Sluiten"
      },
      "labels": {
        "untitledField": "Veld Zonder Titel",
        "required": "Verplicht",
        "newField": "Nieuw"
      }
    },
    "fields": {
      "outlineTitle": "Interviewschema",
      "apiConfig": "API-configuratie",
      "participantInfo": "Deelnemersinformatie",
      "recordAudio": "Audio opnemen",
      "hideIntervieweeTranscript": "Verberg getranscribeerde tekst van geïnterviewde",
      "hideIntervieweeTranscriptHint": "Wanneer ingeschakeld, zien geïnterviewden alleen berichten van de AI-interviewer, niet hun eigen getranscribeerde spraak.",
      "recordVideo": "Video opnemen",
      "voiceEnabled": "AI Stem-interviewer",
      "interactionMode": "Interactiemodus",
      "aiVoice": "AI-stem",
      "maxDuration": "Maximale interviewduur",
      "name": "Naam",
      "age": "Leeftijd",
      "gender": "Geslacht",
      "occupation": "Beroep",
      "education": "Onderwijs",
      "interviewType": "Interviewtype",
      "language": "Taal",
      "transcriptionEngine": "Transcriptie-engine",
      "aiInterviewerName": "Naam van AI-interviewer",
      "aiInterviewerNamePlaceholder": "bijv. Dr. Jansen, Onderzoeksassistent",
      "aiInterviewerNameHelper": "Laat leeg om de standaardnaam te gebruiken op basis van de interviewtaal",
      "interviewerMode": "AI-interviewermodus",
      "interviewerModeHelp": "Kies hoeveel AI-modellen dit interview aansturen.",
      "probingDepth": "Doorvraagdiepte",
      "probingDepthHelp": "Hoe sterk de AI doorvraagt op elk antwoord."
    },
    "probingDepth": {
      "lightLabel": "Licht",
      "lightDesc": "0-1 vervolgvragen · prioriteit ligt op dekking · snel tempo",
      "moderateLabel": "Gemiddeld (aanbevolen)",
      "moderateDesc": "1-2 vervolgvragen · gebalanceerde diepte en dekking",
      "deepLabel": "Diep",
      "deepDesc": "2-4 vervolgvragen · graaft naar motieven en voorbeelden"
    },
    "interviewerModes": {
      "singleLabel": "Enkel model (sneller)",
      "singleDesc": "Eén AI voert het volledige interview. Lagere latentie en kosten.",
      "dualLabel": "Dubbel model (aanbevolen)",
      "dualDesc": "Een supervisormodel controleert het antwoordmodel voor betere vervolgvragen.",
      "structuredNotice": "Gestructureerde interviews gebruiken altijd het dubbele-modelsysteem."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (aanbevolen, beste nauwkeurigheid)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — beste meertalige nauwkeurigheid in 99 talen. Valt automatisch terug op Google → Whisper → Xunfei indien nodig.",
      "autoSelectedElevenLabs": "✓ ElevenLabs automatisch geselecteerd",
      "voicePicker": {
        "autoLabel": "Auto (aanbevolen)",
        "autoDesc": "ElevenLabs Bella · meertalige vrouwenstem · alle 10 talen",
        "warmFemaleMultilingual": "Warme vrouw · meertalig",
        "calmFemaleMultilingual": "Rustige vrouw · meertalig",
        "youngFemaleMultilingual": "Jonge vrouw · meertalig",
        "matureMaleMultilingual": "Volwassen man · meertalig",
        "warmMaleMultilingual": "Warme man · meertalig",
        "casualMaleMultilingual": "Casual man · meertalig",
        "mimoDesc": "Native Chinese vrouw · beste voor Mandarijn",
        "openaiDesc": "Neutrale stem · meertalig",
        "xunfeiDesc": "Chinese vrouw (oud)",
        "recommendedBadge": "AANBEVOLEN",
        "orPickSpecific": "Of kies een specifieke stem:",
        "filterAll": "Alle stemmen",
        "filterFemale": "Vrouwelijk",
        "filterMale": "Mannelijk",
        "filterChinese": "Beste voor Chinees",
        "noMatch": "Geen stemmen voldoen aan dit filter."
      },
      "google": "Google Speech (aanbevolen, beste nauwkeurigheid)",
      "whisper": "Whisper (snel, goede nauwkeurigheid)",
      "xunfei": "Xunfei (traag, niet langer aanbevolen)",
      "helperChinese": "Google Speech (aanbevolen) — beste nauwkeurigheid op Mandarijn. Whisper is sneller en goedkoper. Xunfei alleen als reserve.",
      "helperDefault": "Whisper (aanbevolen) — snel en kostenefficiënt. Google Speech biedt hogere nauwkeurigheid tegen hogere kosten.",
      "autoSelectedXunfei": "✓ Xunfei automatisch geselecteerd voor Chinees",
      "autoSelectedGoogle": "✓ Google automatisch geselecteerd voor Chinees",
      "autoSelectedWhisper": "✓ Whisper automatisch geselecteerd"
    },
    "interactionModes": {
      "textToText": "Tekst naar tekst",
      "textToVoice": "Tekst naar stem + tekst",
      "voiceToText": "Stem naar tekst",
      "voiceToVoice": "Stem naar stem + tekst",
      "textToTextDesc": "Typ uw antwoorden, lees AI-antwoorden (Geen audio)",
      "textToVoiceDesc": "Typ uw antwoorden, AI reageert met stem + tekst",
      "voiceToTextDesc": "Spreek uw antwoorden, lees AI-antwoorden (Geen audio)",
      "voiceToVoiceDesc": "Spreek uw antwoorden, AI reageert met stem + tekst"
    },
    "actions": {
      "saveOutline": "Schema opslaan",
      "updateApiKey": "API-sleutel bijwerken",
      "downloadData": "Interviewgegevens downloaden",
      "liveSupervision": "Live supervisie",
      "downloadCsv": "CSV downloaden",
      "deleteParticipant": "Deelnemer verwijderen",
      "addKey": "API-sleutel toevoegen",
      "saveRecordingPreferences": "Opnamevoorkeuren opslaan",
      "saveInterviewSettings": "Interview-instellingen opslaan",
      "saveGeneralSettings": "Algemene instellingen opslaan",
      "saveStandardModeSettings": "Standaardmodus-instellingen opslaan",
      "viewInterviewGuide": "Bekijk interviewgids",
      "editProjectName": "Projectnaam bewerken",
      "findParticipants": "Deelnemers Vinden",
      "testWithSynthetic": "Test met synthetisch mens",
      "classicView": "Klassieke Weergave",
      "newView": "Nieuwe Indeling"
    },
    "interviewTypes": {
      "structured": "Gestructureerd interview",
      "semiStructured": "Semi-gestructureerd interview"
    },
    "languages": {
      "english": "Engels (English)",
      "chinese": "Chinees (Chinese)",
      "french": "Frans (Français)",
      "norwegian": "Noors (Norsk)",
      "dutch": "Nederlands (Dutch)",
      "german": "Duits (Deutsch)",
      "spanish": "Spaans (Español)",
      "portuguese": "Portugees (Português)",
      "japanese": "Japans (日本語)",
      "arabic": "Arabisch (العربية)",
      "flexible": "Flexibel",
      "flexibleHelp": "Laat uw geïnterviewde hun voorkeurstaal selecteren"
    },
    "messages": {
      "outlineSaved": "Schema succesvol opgeslagen",
      "outlineSaveFailed": "Schema opslaan mislukt",
      "saveFailed": "Schema opslaan mislukt",
      "apiKeyUpdated": "API-sleutel succesvol bijgewerkt",
      "apiKeyError": "API-sleutel bijwerken mislukt",
      "downloadStarted": "Download gestart",
      "downloadComplete": "Download voltooid",
      "downloadFailed": "Download mislukt",
      "noParticipants": "Geen deelnemers gevonden",
      "deleteConfirm": "Weet je zeker dat je deze deelnemer wilt verwijderen?",
      "deleteSuccess": "Deelnemer succesvol verwijderd",
      "deleteFailed": "Deelnemer verwijderen mislukt",
      "confirmDeleteParticipant": "Weet je zeker dat je deze deelnemer wilt verwijderen?",
      "participantDeleted": "Deelnemer succesvol verwijderd",
      "participantDeleteFailed": "Deelnemer verwijderen mislukt",
      "notFound": "Project niet gevonden",
      "noKey": "Geen API-sleutel geconfigureerd",
      "settingsSaved": "Interview-instellingen succesvol opgeslagen",
      "settingsScopeNote": "Deze instellingen gelden alleen voor de interviewlink in Standard Mode. De link voor Realtime Audio (experimenteel) heeft zijn eigen instellingen voor stem, reactietempo en onderbreking (in het paneel Realtime spraakmodus hierboven); de interactiemodus, het transcriptie-engine, de interviewermodus, de doorvraagdiepte en de naam van de AI-interviewer gelden alleen voor Standard Mode.",
      "generalSettingsScopeNote": "Deze instellingen gelden voor zowel de standaardmodus-interviewlink als de realtime-audio (experimentele) link.",
      "settingsError": "Interview-instellingen opslaan mislukt",
      "loadError": "Kan projectdetails niet laden, probeer het later opnieuw",
      "timeoutError": "Verzoek time-out - controleer je verbinding",
      "fetchError": "Projectdetails laden mislukt",
      "participantsTimeout": "Deelnemersgegevens laden mislukt vanwege time-out, controleer je verbinding",
      "participantsError": "Deelnemers ophalen mislukt",
      "participantsPagination": "Totaal {{total}} deelnemers, pagina {{page}} van {{pages}}",
      "dataVisualization": "Gegevensverkenning",
      "themeAnalysisLens": "Thema-analyse met ThemeLens",
      "inductiveCoding": "AI Inductieve Codering",
      "optimizeOutline": "AI Schema Optimaliseren",
      "optimizationResult": "Optimalisatieresultaat",
      "optimizedOutline": "Geoptimaliseerd Schema:",
      "improvements": "Belangrijkste verbeteringen:",
      "suggestions": "Suggesties:",
      "applyOptimization": "Optimalisatie toepassen",
      "startOptimization": "Optimalisatie starten",
      "optimizationGoals": "Optimalisatiedoelen (Optioneel)",
      "optimizationGoalsPlaceholder": "bijv.: Vraagdiepte verhogen, vraagopening verbeteren, vraaglogica optimaliseren, etc.",
      "unsavedChanges": "Je hebt niet-opgeslagen wijzigingen",
      "changesDiscarded": "Wijzigingen verworpen",
      "optimizing": "Optimaliseren...",
      "recordingPreferencesSaved": "Opnamevoorkeuren opgeslagen",
      "recordingPreferencesSaveFailed": "Opnamevoorkeuren opslaan mislukt",
      "outlineRequired": "Schema is vereist",
      "outlineOptimized": "Schema geoptimaliseerd",
      "outlineOptimizeFailed": "Schema optimalisatie mislukt",
      "optimizedOutlineApplied": "Geoptimaliseerd schema toegepast",
      "projectNameSaved": "Projectnaam succesvol opgeslagen",
      "projectNameSaveFailed": "Projectnaam opslaan mislukt",
      "unsavedProjectNameChanges": "Je hebt niet-opgeslagen wijzigingen aan de projectnaam",
      "shareLinkInfo": "Deel deze links met je geïnterviewden. Ze kunnen direct toegang krijgen tot het interview zonder in te loggen.",
      "standardLinkCopied": "Standaard modus link gekopieerd naar klembord",
      "realtimeLinkCopied": "Realtime modus link gekopieerd naar klembord",
      "selectInterviewFirst": "Selecteer eerst een interview."
    },
    "downloads": {
      "title": "Downloadopties",
      "description": "Kies wat je wilt downloaden:",
      "downloadEverything": "Alles downloaden",
      "downloadMetadataOnly": "Alleen metadata downloaden",
      "downloadAudioForSpecificInterview": "Audio downloaden voor een specifiek interview",
      "interviewLabel": "Interview",
      "downloadSelectedInterviewAudio": "Audio van het geselecteerde interview downloaden"
    },
    "warnings": {
      "audioRecordingEnabled": "Waarschuwing: Audio-opname is ingeschakeld voor dit project. Stemmen van deelnemers worden opgenomen tijdens interviews.",
      "audioInterviewNotice": "⚠️ Experimentele functie: Dit is nieuwe technologie met lagere nauwkeurigheid."
    },
    "buttons": {
      "testWithSynthetic": "Interview testen met synthetisch mens"
    },
    "linkTypes": {
      "standardMode": "Standaard Interviewmodus",
      "standardDescription": "Ondersteunt 4 modaliteiten (spraak-naar-spraak, spraak-naar-tekst, tekst-naar-spraak, tekst-naar-tekst). Aanbevolen.",
      "realtimeMode": "Realtime Spraak Modus (Experimenteel)",
      "realtimeDescription": "Spraakgebaseerd interview met realtime AI-gesprek. Nieuwe technologie."
    },
    "realtimeSettings": {
      "title": "Realtime spraakinstellingen",
      "description": "Pas het realtime audio-interview aan. De standaardwaarden komen overeen met de vorige versie.",
      "voiceLabel": "AI-stem",
      "voiceDefaultTag": "standaard",
      "voiceNaturalTag": "natuurlijker",
      "pacingLabel": "Reactietempo",
      "pacingQuick": "Snel — reageert vlot",
      "pacingStandard": "Standaard (standaardinstelling)",
      "pacingPatient": "Geduldig — wacht langer",
      "pacingHelp": "Hoe lang de AI wacht nadat de deelnemer stopt met praten voordat hij reageert.",
      "allowInterruptionLabel": "Deelnemers toestaan de AI te onderbreken",
      "allowInterruptionHelp": "Aan: deelnemers kunnen door de AI heen praten. Uit: de AI praat eerst uit voordat hij luistert — voorkomt dat hij wordt onderbroken door echo van laptopluidsprekers.",
      "save": "Realtime instellingen opslaan",
      "saved": "Realtime instellingen opgeslagen",
      "saveFailed": "Realtime instellingen opslaan mislukt"
    },
    "currentSettings": "Huidige instellingen: {{type}}, Taal: {{language}}",
    "collaboration": {
      "title": "Team",
      "invite": "Medewerker uitnodigen",
      "inviteLabel": "E-mailadres",
      "inviteButton": "Uitnodigen",
      "inviting": "Verzenden...",
      "inviteSuccess": "Uitnodiging verzonden!",
      "inviteError": "Uitnodiging verzenden mislukt. Probeer opnieuw.",
      "alreadyInvited": "Dit e-mailadres is al uitgenodigd.",
      "collaborators": "Medewerkers",
      "noCollaborators": "Nog geen medewerkers. Nodig iemand uit om te beginnen.",
      "statusPending": "In afwachting",
      "statusActive": "Actief",
      "remove": "Verwijderen",
      "removeConfirm": "Deze medewerker verwijderen?",
      "sharedBy": "Gedeeld door {{owner}}",
      "surveyHint": "Nodig mensen uit voor dit project. Medewerkers kunnen alle enquêtes bekijken en bewerken.",
      "close": "Sluiten"
    }
  },
  de: {
    "tutorial": {
      "title": "Erste Schritte",
      "step1Title": "Richten Sie Ihren KI-Interviewer ein",
      "step1Body": "Hier konfigurieren Sie die KI, die Ihre Teilnehmer interviewt — ihre Fragen, ihre Persona und wie sie nachfragt.",
      "step2Title": "Sprache oder Text & Sprache wählen",
      "step2Body": "Legen Sie fest, wie Teilnehmer antworten — Text, Sprache-zu-Text oder Echtzeit-Sprache — und stellen Sie die KI-Stimme und die Interviewsprache ein.",
      "step3Title": "Link kopieren & teilen",
      "step3Body": "Teilen Sie den Interview-Link mit Ihren Teilnehmern über den Bereich „Links teilen“. Die KI führt jedes Interview durch und transkribiert es automatisch.",
      "step4Title": "Ergebnisse eintreffen sehen",
      "step4Body": "Sobald Teilnehmer fertig sind, öffnen Sie die Analyse, um Transkripte und Themen zu prüfen — oder senden Sie sie für eine tiefere Kodierung an den QDA-Arbeitsbereich.",
      "back": "Zurück",
      "next": "Weiter",
      "done": "Loslegen",
      "skip": "Überspringen",
      "help": "So funktioniert es"
    },
    "loading": "Lädt...",
    "sections": {
      "overview": "Projektübersicht",
      "outline": "Interview-Übersicht",
      "participants": "Teilnehmer",
      "data": "Interviewdaten",
      "settings": "Projekteinstellungen",
      "title": "API-Konfiguration",
      "recordingOptions": "Aufnahmeoptionen",
      "interviewSettings": "Interview-Einstellungen",
      "generalSettings": "Allgemeine Einstellungen",
      "standardModeSettings": "Einstellungen für den Standardmodus",
      "outlineHelp": "Hilfe beim Schreiben der Übersicht? Interview-Leitfaden ansehen",
      "customForm": "Benutzerdefiniertes Interviewformular",
      "projectSettings": "Projekteinstellungen",
      "contentConfiguration": "Inhaltskonfiguration",
      "dataAnalytics": "Daten & Analytik",
      "customFormDescription": "Erstellen Sie ein benutzerdefiniertes Formular, um zusätzliche Informationen von Befragten vor Beginn des Interviews zu sammeln.",
      "shareLinks": "Interview-Links teilen",
      "navigation": "Navigation",
      "digitalTwinConsent": "Einwilligung zum digitalen Zwilling einholen",
      "digitalTwinConsentDescription": "Wenn aktiviert, werden die Teilnehmer gefragt, ob sie der Verwendung ihrer Interviewdaten zur Erstellung einer KI-Persona (digitaler Zwilling) für zukünftige Forschung zustimmen."
    },
    "formBuilder": {
      "title": "Formular-Builder",
      "previewForm": "Formular-Vorschau",
      "saveForm": "Formular speichern",
      "saving": "Speichert...",
      "formFields": "Formularfelder",
      "noFieldsTitle": "Noch keine benutzerdefinierten Felder hinzugefügt",
      "noFieldsDesc1": "Leer lassen, um das Standardformular zu verwenden (Name, Alter, Geschlecht, Beruf, Bildung, Kontakt)",
      "noFieldsDesc2": "Oder klicken Sie auf einen Feldtyp oben, um ein benutzerdefiniertes Interviewformular zu erstellen",
      "addFirstField": "Fügen Sie Ihr erstes Feld hinzu",
      "categories": {
        "input": "Eingabefelder",
        "choice": "Auswahlfelder",
        "display": "Anzeigeelemente"
      },
      "fieldTypes": {
        "text": "Texteingabe",
        "textarea": "Langer Text",
        "email": "E-Mail",
        "phone": "Telefon",
        "number": "Nummer",
        "date": "Datum",
        "file": "Datei-Upload",
        "select": "Dropdown",
        "radio": "Radio-Buttons",
        "checkbox": "Checkboxen",
        "display_text": "Textblock",
        "display_image": "Bild",
        "display_video": "Video",
        "section_header": "Abschnittsüberschrift",
        "page_break": "Seitenumbruch"
      },
      "fieldDescriptions": {
        "text": "Einzeilige Texteingabe",
        "textarea": "Mehrzeiliger Textbereich",
        "email": "E-Mail-Adresse eingeben",
        "phone": "Telefonnummer eingeben",
        "number": "Numerische Eingabe",
        "date": "Datumsauswahl",
        "file": "Datei-Upload-Feld",
        "select": "Dropdown-Auswahl",
        "radio": "Einzelauswahl aus Optionen",
        "checkbox": "Mehrfachauswahl-Optionen",
        "display_text": "Text oder HTML anzeigen",
        "display_image": "Bild anzeigen",
        "display_video": "Video-Inhalt einbetten",
        "section_header": "Abschnittstitel",
        "page_break": "Neue Seite beginnen"
      },
      "fieldEditor": {
        "title": "Feldeigenschaften",
        "fieldLabel": "Feldbezeichnung",
        "fieldType": "Feldtyp",
        "placeholderText": "Platzhaltertext",
        "placeholderHelp": "Platzhaltertext wird im Eingabefeld als Hinweis für Benutzer angezeigt. Er verschwindet, wenn sie zu tippen beginnen.",
        "helpText": "Hilfetext",
        "helpTextHelper": "Optionaler Hilfetext zur Anleitung der Benutzer",
        "required": "Pflichtfeld",
        "options": "Optionen",
        "addOption": "Option hinzufügen",
        "removeOption": "Option entfernen",
        "imageUrl": "Bild-URL",
        "altText": "Alt-Text",
        "videoUrl": "Video-URL",
        "videoUrlHelper": "YouTube, Vimeo oder direkte Video-Datei-URL",
        "displayContent": "Anzuzeigender Inhalt",
        "displayContentHelper": "Unterstützt grundlegende HTML-Tags",
        "textareaRows": "Anzahl der Zeilen",
        "fileAccept": "Akzeptierte Dateitypen",
        "maxFileSize": "Maximale Dateigröße (MB)"
      },
      "defaults": {
        "displayTextContent": "Ihr Anzeigetext hier...",
        "sectionTitle": "Abschnittstitel"
      },
      "validation": {
        "emailAutoApplied": "E-Mail-Validierung wird automatisch angewendet",
        "numberAutoApplied": "Nummernvalidierung wird automatisch angewendet",
        "phoneAutoApplied": "Telefonnummernvalidierung wird automatisch angewendet"
      },
      "actions": {
        "duplicate": "Duplizieren",
        "moveUp": "Nach oben",
        "moveDown": "Nach unten",
        "delete": "Löschen",
        "close": "Schließen"
      },
      "labels": {
        "untitledField": "Unbenanntes Feld",
        "required": "Erforderlich",
        "newField": "Neu"
      }
    },
    "fields": {
      "outlineTitle": "Interview-Übersicht",
      "apiConfig": "API-Konfiguration",
      "participantInfo": "Teilnehmerinformationen",
      "recordAudio": "Audio aufnehmen",
      "hideIntervieweeTranscript": "Transkribierter Text des Befragten ausblenden",
      "hideIntervieweeTranscriptHint": "Wenn aktiviert, sehen Befragte nur die Nachrichten des KI-Interviewers, nicht ihre eigene transkribierte Sprache.",
      "recordVideo": "Video aufnehmen",
      "voiceEnabled": "KI-Sprach-Interviewer",
      "interactionMode": "Interaktionsmodus",
      "aiVoice": "KI-Stimme",
      "maxDuration": "Maximale Interview-Dauer",
      "name": "Name",
      "age": "Alter",
      "gender": "Geschlecht",
      "occupation": "Beruf",
      "education": "Bildung",
      "interviewType": "Interviewtyp",
      "language": "Sprache",
      "transcriptionEngine": "Transkriptions-Engine",
      "aiInterviewerName": "Name des KI-Interviewers",
      "aiInterviewerNamePlaceholder": "z.B. Dr. Müller, Forschungsassistent",
      "aiInterviewerNameHelper": "Leer lassen, um den Standardnamen basierend auf der Interviewsprache zu verwenden",
      "interviewerMode": "KI-Interviewer-Modus",
      "interviewerModeHelp": "Wählen Sie, wie viele KI-Modelle dieses Interview steuern.",
      "probingDepth": "Nachfragetiefe",
      "probingDepthHelp": "Wie konsequent die KI bei jeder Antwort nachfragt."
    },
    "probingDepth": {
      "lightLabel": "Leicht",
      "lightDesc": "0-1 Nachfragen · Schwerpunkt Themenabdeckung · schnelles Tempo",
      "moderateLabel": "Mittel (empfohlen)",
      "moderateDesc": "1-2 Nachfragen · ausgewogene Tiefe und Abdeckung",
      "deepLabel": "Tief",
      "deepDesc": "2-4 Nachfragen · vertieft Motive und konkrete Beispiele"
    },
    "interviewerModes": {
      "singleLabel": "Einzelmodell (schneller)",
      "singleDesc": "Eine KI führt das gesamte Interview. Geringere Latenz und Kosten.",
      "dualLabel": "Dualmodell (empfohlen)",
      "dualDesc": "Ein Supervisor-Modell prüft das Antwortmodell für hochwertigere Nachfragen.",
      "structuredNotice": "Strukturierte Interviews verwenden immer das Dualmodell-System."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (empfohlen, beste Genauigkeit)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — beste mehrsprachige Genauigkeit in 99 Sprachen. Bei Bedarf automatischer Rückfall auf Google → Whisper → Xunfei.",
      "autoSelectedElevenLabs": "✓ ElevenLabs automatisch ausgewählt",
      "voicePicker": {
        "autoLabel": "Auto (empfohlen)",
        "autoDesc": "ElevenLabs Bella · mehrsprachige weibliche Stimme · alle 10 Sprachen",
        "warmFemaleMultilingual": "Warme weibliche Stimme · mehrsprachig",
        "calmFemaleMultilingual": "Ruhige weibliche Stimme · mehrsprachig",
        "youngFemaleMultilingual": "Junge weibliche Stimme · mehrsprachig",
        "matureMaleMultilingual": "Reife männliche Stimme · mehrsprachig",
        "warmMaleMultilingual": "Warme männliche Stimme · mehrsprachig",
        "casualMaleMultilingual": "Lockere männliche Stimme · mehrsprachig",
        "mimoDesc": "Native chinesische Frauenstimme · ideal für Mandarin",
        "openaiDesc": "Neutrale Stimme · mehrsprachig",
        "xunfeiDesc": "Chinesische Frauenstimme (alt)",
        "recommendedBadge": "EMPFOHLEN",
        "orPickSpecific": "Oder eine bestimmte Stimme wählen:",
        "filterAll": "Alle Stimmen",
        "filterFemale": "Weiblich",
        "filterMale": "Männlich",
        "filterChinese": "Am besten für Chinesisch",
        "noMatch": "Keine Stimmen entsprechen diesem Filter."
      },
      "google": "Google Speech (empfohlen, beste Genauigkeit)",
      "whisper": "Whisper (schnell, gute Genauigkeit)",
      "xunfei": "Xunfei (langsam, nicht mehr empfohlen)",
      "helperChinese": "Google Speech (empfohlen) — höchste Genauigkeit für Mandarin. Whisper ist schneller und günstiger. Xunfei nur als Notfall-Option.",
      "helperDefault": "Whisper (empfohlen) — schnell und kostengünstig. Google Speech bietet höhere Genauigkeit zu höheren Kosten.",
      "autoSelectedXunfei": "✓ Xunfei automatisch für Chinesisch ausgewählt",
      "autoSelectedGoogle": "✓ Google automatisch für Chinesisch ausgewählt",
      "autoSelectedWhisper": "✓ Whisper automatisch ausgewählt"
    },
    "interactionModes": {
      "textToText": "Text zu Text",
      "textToVoice": "Text zu Stimme + Text",
      "voiceToText": "Stimme zu Text",
      "voiceToVoice": "Stimme zu Stimme + Text",
      "textToTextDesc": "Tippen Sie Ihre Antworten, lesen Sie KI-Antworten (kein Audio)",
      "textToVoiceDesc": "Tippen Sie Ihre Antworten, KI antwortet mit Stimme + Text",
      "voiceToTextDesc": "Sprechen Sie Ihre Antworten, lesen Sie KI-Antworten (kein Audio)",
      "voiceToVoiceDesc": "Sprechen Sie Ihre Antworten, KI antwortet mit Stimme + Text"
    },
    "actions": {
      "saveOutline": "Übersicht speichern",
      "updateApiKey": "API-Schlüssel aktualisieren",
      "downloadData": "Interviewdaten herunterladen",
      "liveSupervision": "Live-Supervision",
      "downloadCsv": "CSV herunterladen",
      "deleteParticipant": "Teilnehmer löschen",
      "addKey": "API-Schlüssel hinzufügen",
      "saveRecordingPreferences": "Aufnahmeeinstellungen speichern",
      "saveInterviewSettings": "Interview-Einstellungen speichern",
      "saveGeneralSettings": "Allgemeine Einstellungen speichern",
      "saveStandardModeSettings": "Standardmodus-Einstellungen speichern",
      "viewInterviewGuide": "Interview-Leitfaden ansehen",
      "editProjectName": "Projektnamen bearbeiten",
      "findParticipants": "Teilnehmer Finden",
      "testWithSynthetic": "Mit synthetischem Menschen testen",
      "classicView": "Klassische Ansicht",
      "newView": "Neues Layout"
    },
    "interviewTypes": {
      "structured": "Strukturiertes Interview",
      "semiStructured": "Halbstrukturiertes Interview"
    },
    "languages": {
      "english": "Englisch",
      "chinese": "Chinesisch",
      "french": "Französisch (Français)",
      "norwegian": "Norwegisch (Norsk)",
      "dutch": "Niederländisch (Nederlands)",
      "german": "Deutsch",
      "spanish": "Spanisch (Español)",
      "portuguese": "Portugiesisch (Português)",
      "japanese": "Japanisch (日本語)",
      "arabic": "Arabisch (العربية)",
      "flexible": "Flexibel",
      "flexibleHelp": "Lassen Sie Ihren Befragten ihre bevorzugte Sprache auswählen"
    },
    "messages": {
      "outlineSaved": "Übersicht erfolgreich gespeichert",
      "outlineSaveFailed": "Speichern der Übersicht fehlgeschlagen",
      "saveFailed": "Speichern der Übersicht fehlgeschlagen",
      "apiKeyUpdated": "API-Schlüssel erfolgreich aktualisiert",
      "apiKeyError": "API-Schlüssel-Aktualisierung fehlgeschlagen",
      "downloadStarted": "Download gestartet",
      "downloadComplete": "Download abgeschlossen",
      "downloadFailed": "Download fehlgeschlagen",
      "noParticipants": "Keine Teilnehmer gefunden",
      "deleteConfirm": "Sind Sie sicher, dass Sie diesen Teilnehmer löschen möchten?",
      "deleteSuccess": "Teilnehmer erfolgreich gelöscht",
      "deleteFailed": "Löschen des Teilnehmers fehlgeschlagen",
      "notFound": "Projekt nicht gefunden",
      "noKey": "API-Schlüssel nicht konfiguriert",
      "settingsSaved": "Interview-Einstellungen erfolgreich gespeichert",
      "settingsScopeNote": "Diese Einstellungen gelten nur für den Interview-Link im Standard Mode. Der Link für Realtime Audio (experimentell) verfügt über eigene Einstellungen für Stimme, Antworttempo und Unterbrechung (im Bereich Echtzeit-Sprachmodus oben); Interaktionsmodus, Transkriptions-Engine, Interviewer-Modus, Nachfragetiefe und Name des KI-Interviewers gelten nur für den Standard Mode.",
      "generalSettingsScopeNote": "Diese Einstellungen gelten sowohl für den Standardmodus-Interviewlink als auch für den Echtzeit-Audio-Link (experimentell).",
      "settingsError": "Speichern der Interview-Einstellungen fehlgeschlagen",
      "loadError": "Projektdetails konnten nicht geladen werden, bitte versuchen Sie es später erneut",
      "timeoutError": "Zeitüberschreitung der Anfrage - bitte überprüfen Sie Ihre Verbindung",
      "fetchError": "Projektdetails konnten nicht geladen werden",
      "participantsTimeout": "Teilnehmerdaten konnten aufgrund von Zeitüberschreitung nicht geladen werden, bitte überprüfen Sie Ihre Verbindung",
      "participantsError": "Teilnehmer konnten nicht abgerufen werden",
      "participantsPagination": "Gesamt {{total}} Teilnehmer, Seite {{page}} von {{pages}}",
      "dataVisualization": "Datenerkundung",
      "themeAnalysisLens": "Themenanalyse mit ThemeLens",
      "inductiveCoding": "KI-Induktive Kodierung",
      "optimizeOutline": "KI-Übersicht optimieren",
      "optimizationResult": "Optimierungsergebnis",
      "optimizedOutline": "Optimierte Übersicht:",
      "improvements": "Wichtige Verbesserungen:",
      "suggestions": "Vorschläge:",
      "applyOptimization": "Optimierung anwenden",
      "startOptimization": "Optimierung starten",
      "optimizationGoals": "Optimierungsziele (Optional)",
      "optimizationGoalsPlaceholder": "z.B.: Fragentiefe erhöhen, Fragenoffenheit verbessern, Fragenlogik optimieren, etc.",
      "unsavedChanges": "Sie haben nicht gespeicherte Änderungen",
      "changesDiscarded": "Änderungen verworfen",
      "optimizing": "Optimiert...",
      "recordingPreferencesSaved": "Aufnahmeeinstellungen gespeichert",
      "recordingPreferencesSaveFailed": "Speichern der Aufnahmeeinstellungen fehlgeschlagen",
      "outlineRequired": "Übersicht ist erforderlich",
      "outlineOptimized": "Übersicht optimiert",
      "outlineOptimizeFailed": "Übersichtsoptimierung fehlgeschlagen",
      "optimizedOutlineApplied": "Optimierte Übersicht angewendet",
      "projectNameSaved": "Projektname erfolgreich gespeichert",
      "projectNameSaveFailed": "Speichern des Projektnamens fehlgeschlagen",
      "unsavedProjectNameChanges": "Sie haben nicht gespeicherte Änderungen am Projektnamen",
      "shareLinkInfo": "Teilen Sie diese Links mit Ihren Befragten. Sie können direkt ohne Login auf das Interview zugreifen.",
      "standardLinkCopied": "Standard-Modus-Link in Zwischenablage kopiert",
      "realtimeLinkCopied": "Echtzeit-Modus-Link in Zwischenablage kopiert",
      "selectInterviewFirst": "Bitte wählen Sie zuerst ein Interview aus."
    },
    "downloads": {
      "title": "Download-Optionen",
      "description": "Wählen Sie aus, was Sie herunterladen möchten:",
      "downloadEverything": "Alles herunterladen",
      "downloadMetadataOnly": "Nur Metadaten herunterladen",
      "downloadAudioForSpecificInterview": "Audio für ein bestimmtes Interview herunterladen",
      "interviewLabel": "Interview",
      "downloadSelectedInterviewAudio": "Audio des ausgewählten Interviews herunterladen"
    },
    "warnings": {
      "audioRecordingEnabled": "Warnung: Audioaufnahme ist für dieses Projekt aktiviert. Die Stimmen der Teilnehmer werden während der Interviews aufgezeichnet.",
      "audioInterviewNotice": "⚠️ Experimentelle Funktion: Dies ist eine neue Technologie mit niedrigerer Genauigkeit."
    },
    "buttons": {
      "testWithSynthetic": "Interview mit synthetischem Menschen testen"
    },
    "linkTypes": {
      "standardMode": "Standard-Interview-Modus",
      "standardDescription": "Unterstützt 4 Modalitäten (Sprache-zu-Sprache, Sprache-zu-Text, Text-zu-Sprache, Text-zu-Text). Empfohlen.",
      "realtimeMode": "Echtzeit-Stimm-Modus (Experimentell)",
      "realtimeDescription": "Sprachbasiertes Interview mit Echtzeit-KI-Konversation. Neue Technologie."
    },
    "realtimeSettings": {
      "title": "Echtzeit-Spracheinstellungen",
      "description": "Passen Sie das Echtzeit-Audiointerview an. Die Standardwerte entsprechen der vorherigen Version.",
      "voiceLabel": "KI-Stimme",
      "voiceDefaultTag": "Standard",
      "voiceNaturalTag": "natürlicher",
      "pacingLabel": "Antworttempo",
      "pacingQuick": "Schnell — antwortet zügig",
      "pacingStandard": "Standard (Voreinstellung)",
      "pacingPatient": "Geduldig — wartet länger",
      "pacingHelp": "Wie lange die KI wartet, nachdem die teilnehmende Person zu sprechen aufgehört hat, bevor sie antwortet.",
      "allowInterruptionLabel": "Teilnehmenden erlauben, die KI zu unterbrechen",
      "allowInterruptionHelp": "Ein: Teilnehmende können die KI unterbrechen. Aus: Die KI spricht zu Ende, bevor sie zuhört — verhindert, dass sie durch Echo aus Laptop-Lautsprechern abgeschnitten wird.",
      "save": "Echtzeit-Einstellungen speichern",
      "saved": "Echtzeit-Einstellungen gespeichert",
      "saveFailed": "Speichern der Echtzeit-Einstellungen fehlgeschlagen"
    },
    "currentSettings": "Aktuelle Einstellungen: {{type}}, Sprache: {{language}}",
    "collaboration": {
      "title": "Team",
      "invite": "Mitarbeiter einladen",
      "inviteLabel": "E-Mail-Adresse",
      "inviteButton": "Einladen",
      "inviting": "Wird gesendet...",
      "inviteSuccess": "Einladung gesendet!",
      "inviteError": "Einladung konnte nicht gesendet werden. Bitte erneut versuchen.",
      "alreadyInvited": "Diese E-Mail wurde bereits eingeladen.",
      "collaborators": "Mitarbeiter",
      "noCollaborators": "Noch keine Mitarbeiter. Laden Sie jemanden ein, um zu beginnen.",
      "statusPending": "Ausstehend",
      "statusActive": "Aktiv",
      "remove": "Entfernen",
      "removeConfirm": "Diesen Mitarbeiter entfernen?",
      "sharedBy": "Geteilt von {{owner}}",
      "surveyHint": "Laden Sie Personen zu diesem Projekt ein. Mitarbeiter können alle Umfragen ansehen und bearbeiten.",
      "close": "Schließen"
    }
  },
  es: {
    "tutorial": {
      "title": "Primeros pasos",
      "step1Title": "Configura tu entrevistador de IA",
      "step1Body": "Aquí es donde configuras la IA que entrevista a tus participantes — sus preguntas, su personalidad y cómo realiza el seguimiento.",
      "step2Title": "Elige voz o texto e idioma",
      "step2Body": "Decide cómo responden los participantes — texto, voz a texto o voz en tiempo real — y configura la voz de la IA y el idioma de la entrevista.",
      "step3Title": "Copia y comparte tu enlace",
      "step3Body": "Comparte el enlace de tu entrevista con los participantes desde la sección de Enlaces para compartir. La IA realiza cada entrevista y la transcribe automáticamente.",
      "step4Title": "Observa cómo llegan los resultados",
      "step4Body": "A medida que los participantes terminan, abre Analíticas para revisar transcripciones y temas — o envíalos al Espacio de trabajo QDA para una codificación más profunda.",
      "back": "Atrás",
      "next": "Siguiente",
      "done": "Comenzar",
      "skip": "Omitir",
      "help": "Cómo funciona"
    },
    "loading": "Cargando...",
    "sections": {
      "overview": "Resumen del proyecto",
      "outline": "Esquema de entrevista",
      "participants": "Participantes",
      "data": "Datos de entrevista",
      "settings": "Configuración del proyecto",
      "title": "Configuración de API",
      "recordingOptions": "Opciones de grabación",
      "interviewSettings": "Configuración de entrevista",
      "generalSettings": "Ajustes generales",
      "standardModeSettings": "Ajustes del modo estándar",
      "outlineHelp": "¿Necesita ayuda para escribir su esquema? Ver guía de entrevista",
      "customForm": "Formulario de entrevista personalizado",
      "projectSettings": "Configuración del proyecto",
      "contentConfiguration": "Configuración de contenido",
      "dataAnalytics": "Datos y análisis",
      "customFormDescription": "Cree un formulario personalizado para recopilar información adicional de los entrevistados antes de que comience la entrevista.",
      "shareLinks": "Compartir enlaces de entrevista",
      "navigation": "Navegación",
      "digitalTwinConsent": "Recopilar consentimiento para gemelo digital",
      "digitalTwinConsentDescription": "Cuando está habilitado, se preguntará a los participantes si consienten en que sus datos de entrevista se utilicen para crear una persona de IA (gemelo digital) para investigaciones futuras."
    },
    "formBuilder": {
      "title": "Constructor de formularios",
      "previewForm": "Vista previa del formulario",
      "saveForm": "Guardar formulario",
      "saving": "Guardando...",
      "formFields": "Campos del formulario",
      "noFieldsTitle": "Aún no se han agregado campos personalizados",
      "noFieldsDesc1": "Deje esto en blanco para usar el formulario predeterminado (nombre, edad, género, ocupación, educación, contacto)",
      "noFieldsDesc2": "O haga clic en cualquier tipo de campo arriba para crear un formulario de entrevista personalizado",
      "addFirstField": "Agregue su primer campo",
      "categories": {
        "input": "Campos de entrada",
        "choice": "Campos de elección",
        "display": "Elementos de visualización"
      },
      "fieldTypes": {
        "text": "Entrada de texto",
        "textarea": "Texto largo",
        "email": "Correo electrónico",
        "phone": "Teléfono",
        "number": "Número",
        "date": "Fecha",
        "file": "Cargar archivo",
        "select": "Desplegable",
        "radio": "Botones de radio",
        "checkbox": "Casillas de verificación",
        "display_text": "Bloque de texto",
        "display_image": "Imagen",
        "display_video": "Video",
        "section_header": "Encabezado de sección",
        "page_break": "Salto de página"
      },
      "fieldDescriptions": {
        "text": "Entrada de texto de una sola línea",
        "textarea": "Área de texto de múltiples líneas",
        "email": "Entrada de dirección de correo electrónico",
        "phone": "Entrada de número de teléfono",
        "number": "Entrada numérica",
        "date": "Selector de fecha",
        "file": "Campo de carga de archivos",
        "select": "Selección desplegable",
        "radio": "Elección única de opciones",
        "checkbox": "Opciones de elección múltiple",
        "display_text": "Mostrar texto o HTML",
        "display_image": "Mostrar una imagen",
        "display_video": "Insertar contenido de video",
        "section_header": "Título de sección",
        "page_break": "Comenzar nueva página"
      },
      "fieldEditor": {
        "title": "Propiedades del campo",
        "fieldLabel": "Etiqueta del campo",
        "fieldType": "Tipo de campo",
        "placeholderText": "Texto de marcador de posición",
        "placeholderHelp": "El texto del marcador de posición se muestra dentro del campo de entrada como una pista para los usuarios. Desaparece cuando comienzan a escribir.",
        "helpText": "Texto de ayuda",
        "helpTextHelper": "Texto de ayuda opcional para guiar a los usuarios",
        "required": "Campo obligatorio",
        "options": "Opciones",
        "addOption": "Agregar opción",
        "removeOption": "Eliminar opción",
        "imageUrl": "URL de imagen",
        "altText": "Texto alternativo",
        "videoUrl": "URL de video",
        "videoUrlHelper": "YouTube, Vimeo o URL de archivo de video directo",
        "displayContent": "Mostrar contenido",
        "displayContentHelper": "Admite etiquetas HTML básicas",
        "textareaRows": "Número de filas",
        "fileAccept": "Tipos de archivo aceptados",
        "maxFileSize": "Tamaño máximo de archivo (MB)"
      },
      "defaults": {
        "displayTextContent": "Su texto de visualización aquí...",
        "sectionTitle": "Título de sección"
      },
      "validation": {
        "emailAutoApplied": "Se aplicará automáticamente la validación de correo electrónico",
        "numberAutoApplied": "Se aplicará automáticamente la validación de números",
        "phoneAutoApplied": "Se aplicará automáticamente la validación de número de teléfono"
      },
      "actions": {
        "duplicate": "Duplicar",
        "moveUp": "Mover arriba",
        "moveDown": "Mover abajo",
        "delete": "Eliminar",
        "close": "Cerrar"
      },
      "labels": {
        "untitledField": "Campo sin título",
        "required": "Obligatorio",
        "newField": "Nuevo"
      }
    },
    "fields": {
      "outlineTitle": "Esquema de entrevista",
      "apiConfig": "Configuración de API",
      "participantInfo": "Información del participante",
      "recordAudio": "Grabar audio",
      "hideIntervieweeTranscript": "Ocultar texto transcrito del entrevistado",
      "hideIntervieweeTranscriptHint": "Cuando está activado, los entrevistados solo verán los mensajes del entrevistador IA, no su propio discurso transcrito.",
      "recordVideo": "Grabar video",
      "voiceEnabled": "Entrevistador de voz IA",
      "interactionMode": "Modo de interacción",
      "aiVoice": "Voz de IA",
      "maxDuration": "Duración máxima de entrevista",
      "name": "Nombre",
      "age": "Edad",
      "gender": "Género",
      "occupation": "Ocupación",
      "education": "Educación",
      "interviewType": "Tipo de entrevista",
      "language": "Idioma",
      "transcriptionEngine": "Motor de transcripción",
      "aiInterviewerName": "Nombre del entrevistador IA",
      "aiInterviewerNamePlaceholder": "ej. Dr. García, Asistente de investigación",
      "aiInterviewerNameHelper": "Dejar vacío para usar el nombre predeterminado según el idioma de la entrevista",
      "interviewerMode": "Modo del entrevistador IA",
      "interviewerModeHelp": "Elige cuántos modelos de IA dirigen esta entrevista.",
      "probingDepth": "Profundidad de sondeo",
      "probingDepthHelp": "Con qué insistencia la IA repregunta tras cada respuesta."
    },
    "probingDepth": {
      "lightLabel": "Ligera",
      "lightDesc": "0-1 repreguntas · prioriza la cobertura · ritmo rápido",
      "moderateLabel": "Moderada (recomendada)",
      "moderateDesc": "1-2 repreguntas · equilibrio entre profundidad y cobertura",
      "deepLabel": "Profunda",
      "deepDesc": "2-4 repreguntas · explora motivaciones y ejemplos concretos"
    },
    "interviewerModes": {
      "singleLabel": "Modelo único (más rápido)",
      "singleDesc": "Una IA gestiona toda la entrevista. Menor latencia y coste.",
      "dualLabel": "Modelo dual (recomendado)",
      "dualDesc": "Un modelo supervisor audita al modelo respondedor para preguntas de seguimiento de mayor calidad.",
      "structuredNotice": "Las entrevistas estructuradas siempre usan el sistema de modelo dual."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (recomendado, mayor precisión)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — mejor precisión multilingüe en 99 idiomas. Recurre automáticamente a Google → Whisper → Xunfei si falla.",
      "autoSelectedElevenLabs": "✓ ElevenLabs seleccionado automáticamente",
      "voicePicker": {
        "autoLabel": "Auto (recomendado)",
        "autoDesc": "ElevenLabs Bella · voz femenina multilingüe · 10 idiomas",
        "warmFemaleMultilingual": "Femenina cálida · multilingüe",
        "calmFemaleMultilingual": "Femenina serena · multilingüe",
        "youngFemaleMultilingual": "Femenina joven · multilingüe",
        "matureMaleMultilingual": "Masculina madura · multilingüe",
        "warmMaleMultilingual": "Masculina cálida · multilingüe",
        "casualMaleMultilingual": "Masculina relajada · multilingüe",
        "mimoDesc": "Voz femenina china nativa · ideal para mandarín",
        "openaiDesc": "Voz neutra · multilingüe",
        "xunfeiDesc": "Voz femenina china (heredada)",
        "recommendedBadge": "RECOMENDADO",
        "orPickSpecific": "O elige una voz específica:",
        "filterAll": "Todas las voces",
        "filterFemale": "Femenina",
        "filterMale": "Masculina",
        "filterChinese": "Mejor para chino",
        "noMatch": "Ninguna voz coincide con este filtro."
      },
      "google": "Google Speech (recomendado, mayor precisión)",
      "whisper": "Whisper (rápido, buena precisión)",
      "xunfei": "Xunfei (lento, ya no recomendado)",
      "helperChinese": "Google Speech (recomendado) — mayor precisión en mandarín. Whisper es más rápido y económico. Xunfei se mantiene solo como respaldo.",
      "helperDefault": "Whisper (recomendado) — rápido y rentable. Google Speech ofrece mayor precisión a un costo más alto.",
      "autoSelectedXunfei": "✓ Xunfei seleccionado automáticamente para chino",
      "autoSelectedGoogle": "✓ Google seleccionado automáticamente para chino",
      "autoSelectedWhisper": "✓ Whisper seleccionado automáticamente"
    },
    "interactionModes": {
      "textToText": "Texto a texto",
      "textToVoice": "Texto a voz + texto",
      "voiceToText": "Voz a texto",
      "voiceToVoice": "Voz a voz + texto",
      "textToTextDesc": "Escriba sus respuestas, lea respuestas de IA (sin audio)",
      "textToVoiceDesc": "Escriba sus respuestas, la IA responde con voz + texto",
      "voiceToTextDesc": "Hable sus respuestas, lea respuestas de IA (sin audio)",
      "voiceToVoiceDesc": "Hable sus respuestas, la IA responde con voz + texto"
    },
    "actions": {
      "saveOutline": "Guardar esquema",
      "updateApiKey": "Actualizar clave API",
      "downloadData": "Descargar datos de entrevista",
      "liveSupervision": "Supervisión en vivo",
      "downloadCsv": "Descargar CSV",
      "deleteParticipant": "Eliminar participante",
      "addKey": "Agregar clave API",
      "saveRecordingPreferences": "Guardar preferencias de grabación",
      "saveInterviewSettings": "Guardar configuración de entrevista",
      "saveGeneralSettings": "Guardar ajustes generales",
      "saveStandardModeSettings": "Guardar ajustes del modo estándar",
      "viewInterviewGuide": "Ver guía de entrevista",
      "editProjectName": "Editar nombre del proyecto",
      "findParticipants": "Encontrar Participantes",
      "testWithSynthetic": "Probar con humano sintético",
      "classicView": "Vista Clásica",
      "newView": "Nuevo Diseño"
    },
    "interviewTypes": {
      "structured": "Entrevista estructurada",
      "semiStructured": "Entrevista semiestructurada"
    },
    "languages": {
      "english": "Inglés",
      "chinese": "Chino",
      "french": "Francés (Français)",
      "norwegian": "Noruego (Norsk)",
      "dutch": "Holandés (Nederlands)",
      "german": "Alemán (Deutsch)",
      "spanish": "Español",
      "portuguese": "Portugués (Português)",
      "japanese": "Japonés (日本語)",
      "arabic": "Árabe (العربية)",
      "flexible": "Flexible",
      "flexibleHelp": "Permita que su entrevistado seleccione su idioma preferido"
    },
    "messages": {
      "outlineSaved": "Esquema guardado exitosamente",
      "outlineSaveFailed": "Error al guardar esquema",
      "saveFailed": "Error al guardar esquema",
      "apiKeyUpdated": "Clave API actualizada exitosamente",
      "apiKeyError": "Error al actualizar clave API",
      "downloadStarted": "Descarga iniciada",
      "downloadComplete": "Descarga completada",
      "downloadFailed": "Descarga fallida",
      "noParticipants": "No se encontraron participantes",
      "deleteConfirm": "¿Está seguro de que desea eliminar este participante?",
      "deleteSuccess": "Participante eliminado exitosamente",
      "deleteFailed": "Error al eliminar participante",
      "notFound": "Proyecto no encontrado",
      "noKey": "Clave API no configurada",
      "settingsSaved": "Configuración de entrevista guardada exitosamente",
      "settingsScopeNote": "Esta configuración solo se aplica al enlace de entrevista del Modo estándar. El enlace de Audio en tiempo real (experimental) tiene su propia configuración de voz, ritmo de respuesta e interrupción (en el panel de Modo de voz en tiempo real de arriba); el modo de interacción, el motor de transcripción, el modo de entrevistador, la profundidad de indagación y el nombre del entrevistador de IA se aplican únicamente al Modo estándar.",
      "generalSettingsScopeNote": "Estos ajustes se aplican tanto al enlace de entrevista en modo estándar como al enlace de audio en tiempo real (experimental).",
      "settingsError": "Error al guardar configuración de entrevista",
      "loadError": "Error al cargar detalles del proyecto, por favor intente más tarde",
      "timeoutError": "Tiempo de espera de solicitud agotado - por favor verifique su conexión",
      "fetchError": "Error al cargar detalles del proyecto",
      "participantsTimeout": "Error al cargar datos de participantes debido a tiempo de espera agotado, por favor verifique su conexión",
      "participantsError": "Error al obtener participantes",
      "participantsPagination": "Total {{total}} participantes, página {{page}} de {{pages}}",
      "dataVisualization": "Exploración de datos",
      "themeAnalysisLens": "Análisis temático con ThemeLens",
      "inductiveCoding": "Codificación inductiva IA",
      "optimizeOutline": "Optimizar esquema con IA",
      "optimizationResult": "Resultado de optimización",
      "optimizedOutline": "Esquema optimizado:",
      "improvements": "Mejoras clave:",
      "suggestions": "Sugerencias:",
      "applyOptimization": "Aplicar optimización",
      "startOptimization": "Iniciar optimización",
      "optimizationGoals": "Objetivos de optimización (Opcional)",
      "optimizationGoalsPlaceholder": "ej.: Aumentar profundidad de preguntas, mejorar apertura de preguntas, optimizar lógica de preguntas, etc.",
      "unsavedChanges": "Tiene cambios sin guardar",
      "changesDiscarded": "Cambios descartados",
      "optimizing": "Optimizando...",
      "recordingPreferencesSaved": "Preferencias de grabación guardadas",
      "recordingPreferencesSaveFailed": "Error al guardar preferencias de grabación",
      "outlineRequired": "Se requiere esquema",
      "outlineOptimized": "Esquema optimizado",
      "outlineOptimizeFailed": "Error al optimizar esquema",
      "optimizedOutlineApplied": "Esquema optimizado aplicado",
      "projectNameSaved": "Nombre del proyecto guardado exitosamente",
      "projectNameSaveFailed": "Error al guardar nombre del proyecto",
      "unsavedProjectNameChanges": "Tiene cambios sin guardar en el nombre del proyecto",
      "shareLinkInfo": "Comparta estos enlaces con sus entrevistados. Pueden acceder a la entrevista directamente sin iniciar sesión.",
      "standardLinkCopied": "Enlace de modo estándar copiado al portapapeles",
      "realtimeLinkCopied": "Enlace de modo en tiempo real copiado al portapapeles",
      "selectInterviewFirst": "Por favor, seleccione una entrevista primero."
    },
    "downloads": {
      "title": "Opciones de descarga",
      "description": "Elija lo que desea descargar:",
      "downloadEverything": "Descargar todo",
      "downloadMetadataOnly": "Descargar solo metadatos",
      "downloadAudioForSpecificInterview": "Descargar audio de una entrevista específica",
      "interviewLabel": "Entrevista",
      "downloadSelectedInterviewAudio": "Descargar audio de la entrevista seleccionada"
    },
    "warnings": {
      "audioRecordingEnabled": "Advertencia: La grabación de audio está habilitada para este proyecto. Las voces de los participantes se grabarán durante las entrevistas.",
      "audioInterviewNotice": "⚠️ Función experimental: Esta es una nueva tecnología con menor precisión."
    },
    "buttons": {
      "testWithSynthetic": "Probar entrevista con humano sintético"
    },
    "linkTypes": {
      "standardMode": "Modo de entrevista estándar",
      "standardDescription": "Admite 4 modalidades (voz a voz, voz a texto, texto a voz, texto a texto). Recomendado.",
      "realtimeMode": "Modo de voz en tiempo real (Experimental)",
      "realtimeDescription": "Entrevista basada en voz con conversación IA en tiempo real. Nueva tecnología."
    },
    "realtimeSettings": {
      "title": "Configuración de voz en tiempo real",
      "description": "Personaliza la entrevista de audio en tiempo real. Los valores predeterminados coinciden con la versión anterior.",
      "voiceLabel": "Voz de IA",
      "voiceDefaultTag": "predeterminada",
      "voiceNaturalTag": "más natural",
      "pacingLabel": "Ritmo de respuesta",
      "pacingQuick": "Rápido — responde con rapidez",
      "pacingStandard": "Estándar (predeterminado)",
      "pacingPatient": "Paciente — espera más tiempo",
      "pacingHelp": "Cuánto tiempo espera la IA después de que el participante deja de hablar antes de responder.",
      "allowInterruptionLabel": "Permitir que los participantes interrumpan a la IA",
      "allowInterruptionHelp": "Activado: los participantes pueden hablar por encima de la IA. Desactivado: la IA termina de hablar antes de escuchar — evita que se corte por el eco de los altavoces del portátil.",
      "save": "Guardar configuración en tiempo real",
      "saved": "Configuración en tiempo real guardada",
      "saveFailed": "No se pudo guardar la configuración en tiempo real"
    },
    "currentSettings": "Configuración actual: {{type}}, Idioma: {{language}}",
    "collaboration": {
      "title": "Equipo",
      "invite": "Invitar colaborador",
      "inviteLabel": "Dirección de correo electrónico",
      "inviteButton": "Invitar",
      "inviting": "Enviando...",
      "inviteSuccess": "¡Invitación enviada!",
      "inviteError": "Error al enviar la invitación. Por favor, inténtelo de nuevo.",
      "alreadyInvited": "Este correo ya ha sido invitado.",
      "collaborators": "Colaboradores",
      "noCollaborators": "Aún no hay colaboradores. Invite a alguien para empezar.",
      "statusPending": "Pendiente",
      "statusActive": "Activo",
      "remove": "Eliminar",
      "removeConfirm": "¿Eliminar este colaborador?",
      "sharedBy": "Compartido por {{owner}}",
      "surveyHint": "Invita a personas a este proyecto. Los colaboradores pueden ver y editar todas sus encuestas.",
      "close": "Cerrar"
    }
  },
  pt: {
    "tutorial": {
      "title": "Primeiros passos",
      "step1Title": "Configure o seu entrevistador de IA",
      "step1Body": "É aqui que você configura a IA que entrevista os seus participantes — as suas perguntas, a sua persona e como ela faz o acompanhamento.",
      "step2Title": "Escolha voz ou texto e idioma",
      "step2Body": "Defina como os participantes respondem — texto, voz para texto ou voz em tempo real — e configure a voz da IA e o idioma da entrevista.",
      "step3Title": "Copie e compartilhe o seu link",
      "step3Body": "Compartilhe o link da sua entrevista com os participantes na seção de Links de Compartilhamento. A IA conduz cada entrevista e a transcreve automaticamente.",
      "step4Title": "Acompanhe os resultados chegando",
      "step4Body": "À medida que os participantes terminam, abra as Análises para revisar transcrições e temas — ou envie-os para o Espaço de Trabalho QDA para uma codificação mais aprofundada.",
      "back": "Voltar",
      "next": "Avançar",
      "done": "Começar",
      "skip": "Pular",
      "help": "Como funciona"
    },
    "loading": "Carregando...",
    "sections": {
      "overview": "Visão geral do projeto",
      "outline": "Roteiro da entrevista",
      "participants": "Participantes",
      "data": "Dados da entrevista",
      "settings": "Configurações do projeto",
      "title": "Configuração da API",
      "recordingOptions": "Opções de gravação",
      "interviewSettings": "Configurações da entrevista",
      "generalSettings": "Configurações gerais",
      "standardModeSettings": "Configurações do modo padrão",
      "outlineHelp": "Precisa de ajuda para escrever seu roteiro? Ver guia de entrevista",
      "customForm": "Formulário de entrevista personalizado",
      "projectSettings": "Configurações do projeto",
      "contentConfiguration": "Configuração de conteúdo",
      "dataAnalytics": "Dados e análises",
      "customFormDescription": "Crie um formulário personalizado para coletar informações adicionais dos entrevistados antes do início da entrevista.",
      "shareLinks": "Compartilhar links de entrevista",
      "navigation": "Navegação",
      "digitalTwinConsent": "Coletar consentimento para gêmeo digital",
      "digitalTwinConsentDescription": "Quando habilitado, os participantes serão perguntados se concordam com o uso dos dados de sua entrevista para criar uma persona de IA (gêmeo digital) para pesquisas futuras."
    },
    "formBuilder": {
      "title": "Construtor de formulários",
      "previewForm": "Visualizar formulário",
      "saveForm": "Salvar formulário",
      "saving": "Salvando...",
      "formFields": "Campos do formulário",
      "noFieldsTitle": "Nenhum campo personalizado adicionado ainda",
      "noFieldsDesc1": "Deixe em branco para usar o formulário padrão (nome, idade, gênero, ocupação, educação, contato)",
      "noFieldsDesc2": "Ou clique em qualquer tipo de campo acima para criar um formulário de entrevista personalizado",
      "addFirstField": "Adicione seu primeiro campo",
      "categories": {
        "input": "Campos de entrada",
        "choice": "Campos de escolha",
        "display": "Elementos de exibição"
      },
      "fieldTypes": {
        "text": "Entrada de texto",
        "textarea": "Texto longo",
        "email": "E-mail",
        "phone": "Telefone",
        "number": "Número",
        "date": "Data",
        "file": "Upload de arquivo",
        "select": "Lista suspensa",
        "radio": "Botões de rádio",
        "checkbox": "Caixas de seleção",
        "display_text": "Bloco de texto",
        "display_image": "Imagem",
        "display_video": "Vídeo",
        "section_header": "Cabeçalho de seção",
        "page_break": "Quebra de página"
      },
      "fieldDescriptions": {
        "text": "Entrada de texto de linha única",
        "textarea": "Área de texto de múltiplas linhas",
        "email": "Entrada de endereço de e-mail",
        "phone": "Entrada de número de telefone",
        "number": "Entrada numérica",
        "date": "Seletor de data",
        "file": "Campo de upload de arquivo",
        "select": "Seleção suspensa",
        "radio": "Escolha única das opções",
        "checkbox": "Opções de múltipla escolha",
        "display_text": "Exibir texto ou HTML",
        "display_image": "Exibir uma imagem",
        "display_video": "Incorporar conteúdo de vídeo",
        "section_header": "Título de seção",
        "page_break": "Iniciar nova página"
      },
      "fieldEditor": {
        "title": "Propriedades do campo",
        "fieldLabel": "Rótulo do campo",
        "fieldType": "Tipo de campo",
        "placeholderText": "Texto de espaço reservado",
        "placeholderHelp": "O texto de espaço reservado é mostrado dentro do campo de entrada como uma dica para os usuários. Desaparece quando eles começam a digitar.",
        "helpText": "Texto de ajuda",
        "helpTextHelper": "Texto de ajuda opcional para orientar os usuários",
        "required": "Campo obrigatório",
        "options": "Opções",
        "addOption": "Adicionar opção",
        "removeOption": "Remover opção",
        "imageUrl": "URL da imagem",
        "altText": "Texto alternativo",
        "videoUrl": "URL do vídeo",
        "videoUrlHelper": "YouTube, Vimeo ou URL de arquivo de vídeo direto",
        "displayContent": "Conteúdo de exibição",
        "displayContentHelper": "Suporta tags HTML básicas",
        "textareaRows": "Número de linhas",
        "fileAccept": "Tipos de arquivo aceitos",
        "maxFileSize": "Tamanho máximo do arquivo (MB)"
      },
      "defaults": {
        "displayTextContent": "Seu texto de exibição aqui...",
        "sectionTitle": "Título da seção"
      },
      "validation": {
        "emailAutoApplied": "A validação de e-mail será aplicada automaticamente",
        "numberAutoApplied": "A validação de número será aplicada automaticamente",
        "phoneAutoApplied": "A validação de número de telefone será aplicada automaticamente"
      },
      "actions": {
        "duplicate": "Duplicar",
        "moveUp": "Mover para cima",
        "moveDown": "Mover para baixo",
        "delete": "Excluir",
        "close": "Fechar"
      },
      "labels": {
        "untitledField": "Campo sem título",
        "required": "Obrigatório",
        "newField": "Novo"
      }
    },
    "fields": {
      "outlineTitle": "Roteiro da entrevista",
      "apiConfig": "Configuração da API",
      "participantInfo": "Informações do participante",
      "recordAudio": "Gravar áudio",
      "hideIntervieweeTranscript": "Ocultar texto transcrito do entrevistado",
      "hideIntervieweeTranscriptHint": "Quando ativado, os entrevistados verão apenas as mensagens do entrevistador IA, não sua própria fala transcrita.",
      "recordVideo": "Gravar vídeo",
      "voiceEnabled": "Entrevistador de voz IA",
      "interactionMode": "Modo de interação",
      "aiVoice": "Voz da IA",
      "maxDuration": "Duração máxima da entrevista",
      "name": "Nome",
      "age": "Idade",
      "gender": "Gênero",
      "occupation": "Ocupação",
      "education": "Educação",
      "interviewType": "Tipo de entrevista",
      "language": "Idioma",
      "transcriptionEngine": "Motor de transcrição",
      "aiInterviewerName": "Nome do entrevistador IA",
      "aiInterviewerNamePlaceholder": "ex. Dr. Silva, Assistente de pesquisa",
      "aiInterviewerNameHelper": "Deixe vazio para usar o nome padrão com base no idioma da entrevista",
      "interviewerMode": "Modo do entrevistador IA",
      "interviewerModeHelp": "Escolha quantos modelos de IA conduzem esta entrevista.",
      "probingDepth": "Profundidade de aprofundamento",
      "probingDepthHelp": "Quão insistentemente a IA faz perguntas de acompanhamento."
    },
    "probingDepth": {
      "lightLabel": "Leve",
      "lightDesc": "0-1 perguntas · prioriza a cobertura · ritmo rápido",
      "moderateLabel": "Moderado (recomendado)",
      "moderateDesc": "1-2 perguntas · equilíbrio entre profundidade e cobertura",
      "deepLabel": "Profundo",
      "deepDesc": "2-4 perguntas · investiga motivações e exemplos"
    },
    "interviewerModes": {
      "singleLabel": "Modelo único (mais rápido)",
      "singleDesc": "Uma IA conduz toda a entrevista. Menor latência e custo.",
      "dualLabel": "Modelo duplo (recomendado)",
      "dualDesc": "Um modelo supervisor audita o modelo respondente para perguntas de acompanhamento de maior qualidade.",
      "structuredNotice": "Entrevistas estruturadas sempre usam o sistema de modelo duplo."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (recomendado, maior precisão)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — melhor precisão multilíngue em 99 idiomas. Recorre automaticamente a Google → Whisper → Xunfei se necessário.",
      "autoSelectedElevenLabs": "✓ ElevenLabs selecionado automaticamente",
      "voicePicker": {
        "autoLabel": "Auto (recomendado)",
        "autoDesc": "ElevenLabs Bella · voz feminina multilíngue · 10 idiomas",
        "warmFemaleMultilingual": "Feminina calorosa · multilíngue",
        "calmFemaleMultilingual": "Feminina serena · multilíngue",
        "youngFemaleMultilingual": "Feminina jovem · multilíngue",
        "matureMaleMultilingual": "Masculina madura · multilíngue",
        "warmMaleMultilingual": "Masculina calorosa · multilíngue",
        "casualMaleMultilingual": "Masculina casual · multilíngue",
        "mimoDesc": "Voz feminina chinesa nativa · ideal para mandarim",
        "openaiDesc": "Voz neutra · multilíngue",
        "xunfeiDesc": "Voz feminina chinesa (legado)",
        "recommendedBadge": "RECOMENDADO",
        "orPickSpecific": "Ou escolha uma voz específica:",
        "filterAll": "Todas as vozes",
        "filterFemale": "Feminina",
        "filterMale": "Masculina",
        "filterChinese": "Melhor para chinês",
        "noMatch": "Nenhuma voz corresponde a este filtro."
      },
      "google": "Google Speech (recomendado, maior precisão)",
      "whisper": "Whisper (rápido, boa precisão)",
      "xunfei": "Xunfei (lento, não mais recomendado)",
      "helperChinese": "Google Speech (recomendado) — maior precisão em mandarim. Whisper é mais rápido e econômico. Xunfei mantido apenas como reserva.",
      "helperDefault": "Whisper (recomendado) — rápido e econômico. Google Speech oferece maior precisão a um custo mais alto.",
      "autoSelectedXunfei": "✓ Xunfei selecionado automaticamente para chinês",
      "autoSelectedGoogle": "✓ Google selecionado automaticamente para chinês",
      "autoSelectedWhisper": "✓ Whisper selecionado automaticamente"
    },
    "interactionModes": {
      "textToText": "Texto para texto",
      "textToVoice": "Texto para voz + texto",
      "voiceToText": "Voz para texto",
      "voiceToVoice": "Voz para voz + texto",
      "textToTextDesc": "Digite suas respostas, leia as respostas da IA (sem áudio)",
      "textToVoiceDesc": "Digite suas respostas, a IA responde com voz + texto",
      "voiceToTextDesc": "Fale suas respostas, leia as respostas da IA (sem áudio)",
      "voiceToVoiceDesc": "Fale suas respostas, a IA responde com voz + texto"
    },
    "actions": {
      "saveOutline": "Salvar roteiro",
      "updateApiKey": "Atualizar chave API",
      "downloadData": "Baixar dados da entrevista",
      "liveSupervision": "Supervisão ao vivo",
      "downloadCsv": "Baixar CSV",
      "deleteParticipant": "Excluir participante",
      "addKey": "Adicionar chave API",
      "saveRecordingPreferences": "Salvar preferências de gravação",
      "saveInterviewSettings": "Salvar configurações da entrevista",
      "saveGeneralSettings": "Salvar configurações gerais",
      "saveStandardModeSettings": "Salvar configurações do modo padrão",
      "viewInterviewGuide": "Ver guia de entrevista",
      "editProjectName": "Editar nome do projeto",
      "findParticipants": "Encontrar participantes",
      "testWithSynthetic": "Testar com humano sintético",
      "classicView": "Visão Clássica",
      "newView": "Novo Layout"
    },
    "interviewTypes": {
      "structured": "Entrevista estruturada",
      "semiStructured": "Entrevista semiestruturada"
    },
    "languages": {
      "english": "Inglês (English)",
      "chinese": "Chinês (Chinese)",
      "french": "Francês (Français)",
      "norwegian": "Norueguês (Norsk)",
      "dutch": "Holandês (Nederlands)",
      "german": "Alemão (Deutsch)",
      "spanish": "Espanhol (Español)",
      "portuguese": "Português (Portuguese)",
      "japanese": "Japonês (Japanese)",
      "arabic": "Árabe (Arabic)",
      "flexible": "Flexível",
      "flexibleHelp": "Deixe seu entrevistado selecionar seu idioma preferido"
    },
    "messages": {
      "outlineSaved": "Roteiro salvo com sucesso",
      "outlineSaveFailed": "Falha ao salvar roteiro",
      "saveFailed": "Falha ao salvar roteiro",
      "apiKeyUpdated": "Chave API atualizada com sucesso",
      "apiKeyError": "Falha ao atualizar chave API",
      "downloadStarted": "Download iniciado",
      "downloadComplete": "Download concluído",
      "downloadFailed": "Download falhou",
      "noParticipants": "Nenhum participante encontrado",
      "deleteConfirm": "Tem certeza que deseja excluir este participante?",
      "deleteSuccess": "Participante excluído com sucesso",
      "deleteFailed": "Falha ao excluir participante",
      "notFound": "Projeto não encontrado",
      "noKey": "Chave API não configurada",
      "settingsSaved": "Configurações da entrevista salvas com sucesso",
      "settingsScopeNote": "Estas configurações aplicam-se apenas ao link de entrevista do Modo Padrão (Standard Mode). O link de Áudio em Tempo Real (Realtime Audio) (experimental) tem as suas próprias configurações de voz, ritmo de resposta e interrupção (no painel Modo de Voz em Tempo Real acima); o modo de interação, o motor de transcrição, o modo de entrevistador, a profundidade de sondagem e o nome do entrevistador de IA aplicam-se apenas ao Modo Padrão (Standard Mode).",
      "generalSettingsScopeNote": "Estas configurações aplicam-se tanto ao link de entrevista no modo Padrão quanto ao link de áudio em tempo real (experimental).",
      "settingsError": "Falha ao salvar configurações da entrevista",
      "loadError": "Falha ao carregar detalhes do projeto, por favor tente novamente mais tarde",
      "timeoutError": "Tempo limite da solicitação - por favor verifique sua conexão",
      "fetchError": "Falha ao carregar detalhes do projeto",
      "participantsTimeout": "Falha ao carregar dados dos participantes devido ao tempo limite, por favor verifique sua conexão",
      "participantsError": "Falha ao buscar participantes",
      "participantsPagination": "Total de {{total}} participantes, página {{page}} de {{pages}}",
      "dataVisualization": "Exploração de dados",
      "themeAnalysisLens": "Análise temática com ThemeLens",
      "inductiveCoding": "Codificação indutiva IA",
      "optimizeOutline": "Otimizar roteiro com IA",
      "optimizationResult": "Resultado da otimização",
      "optimizedOutline": "Roteiro otimizado:",
      "improvements": "Melhorias principais:",
      "suggestions": "Sugestões:",
      "applyOptimization": "Aplicar otimização",
      "startOptimization": "Iniciar otimização",
      "optimizationGoals": "Objetivos de otimização (Opcional)",
      "optimizationGoalsPlaceholder": "ex.: Aumentar profundidade das perguntas, melhorar abertura das perguntas, otimizar lógica das perguntas, etc.",
      "unsavedChanges": "Você tem alterações não salvas",
      "changesDiscarded": "Alterações descartadas",
      "optimizing": "Otimizando...",
      "recordingPreferencesSaved": "Preferências de gravação salvas",
      "recordingPreferencesSaveFailed": "Falha ao salvar preferências de gravação",
      "outlineRequired": "Roteiro é obrigatório",
      "outlineOptimized": "Roteiro otimizado",
      "outlineOptimizeFailed": "Falha na otimização do roteiro",
      "optimizedOutlineApplied": "Roteiro otimizado aplicado",
      "projectNameSaved": "Nome do projeto salvo com sucesso",
      "projectNameSaveFailed": "Falha ao salvar nome do projeto",
      "unsavedProjectNameChanges": "Você tem alterações não salvas no nome do projeto",
      "shareLinkInfo": "Compartilhe estes links com seus entrevistados. Eles podem acessar a entrevista diretamente sem login.",
      "standardLinkCopied": "Link do modo padrão copiado para a área de transferência",
      "realtimeLinkCopied": "Link do modo em tempo real copiado para a área de transferência",
      "selectInterviewFirst": "Por favor, selecione uma entrevista primeiro."
    },
    "downloads": {
      "title": "Opções de download",
      "description": "Escolha o que deseja baixar:",
      "downloadEverything": "Baixar tudo",
      "downloadMetadataOnly": "Baixar apenas metadados",
      "downloadAudioForSpecificInterview": "Baixar áudio de uma entrevista específica",
      "interviewLabel": "Entrevista",
      "downloadSelectedInterviewAudio": "Baixar áudio da entrevista selecionada"
    },
    "warnings": {
      "audioRecordingEnabled": "Aviso: A gravação de áudio está habilitada para este projeto. As vozes dos participantes serão gravadas durante as entrevistas.",
      "audioInterviewNotice": "⚠️ Recurso experimental: Esta é uma nova tecnologia com menor precisão."
    },
    "buttons": {
      "testWithSynthetic": "Testar entrevista com humano sintético"
    },
    "linkTypes": {
      "standardMode": "Modo de entrevista padrão",
      "standardDescription": "Suporta 4 modalidades (voz a voz, voz a texto, texto a voz, texto a texto). Recomendado.",
      "realtimeMode": "Modo de voz em tempo real (Experimental)",
      "realtimeDescription": "Entrevista baseada em voz com conversa IA em tempo real. Nova tecnologia."
    },
    "realtimeSettings": {
      "title": "Configurações de voz em tempo real",
      "description": "Personalize a entrevista por áudio em tempo real. Os padrões correspondem à versão anterior.",
      "voiceLabel": "Voz da IA",
      "voiceDefaultTag": "padrão",
      "voiceNaturalTag": "mais natural",
      "pacingLabel": "Ritmo de resposta",
      "pacingQuick": "Rápido — responde depressa",
      "pacingStandard": "Padrão (predefinido)",
      "pacingPatient": "Paciente — espera mais tempo",
      "pacingHelp": "Quanto tempo a IA espera depois de o participante parar de falar antes de responder.",
      "allowInterruptionLabel": "Permitir que os participantes interrompam a IA",
      "allowInterruptionHelp": "Ativado: os participantes podem falar por cima da IA. Desativado: a IA termina de falar antes de ouvir — evita que seja cortada pelo eco das colunas do portátil.",
      "save": "Guardar configurações em tempo real",
      "saved": "Configurações em tempo real guardadas",
      "saveFailed": "Falha ao guardar as configurações em tempo real"
    },
    "currentSettings": "Configurações atuais: {{type}}, Idioma: {{language}}",
    "collaboration": {
      "title": "Equipe",
      "invite": "Convidar colaborador",
      "inviteLabel": "Endereço de e-mail",
      "inviteButton": "Convidar",
      "inviting": "Enviando...",
      "inviteSuccess": "Convite enviado!",
      "inviteError": "Falha ao enviar convite. Por favor, tente novamente.",
      "alreadyInvited": "Este e-mail já foi convidado.",
      "collaborators": "Colaboradores",
      "noCollaborators": "Nenhum colaborador ainda. Convide alguém para começar.",
      "statusPending": "Pendente",
      "statusActive": "Ativo",
      "remove": "Remover",
      "removeConfirm": "Remover este colaborador?",
      "sharedBy": "Compartilhado por {{owner}}",
      "surveyHint": "Convide pessoas para este projeto. Os colaboradores podem ver e editar todas as suas pesquisas.",
      "close": "Fechar"
    }
  },
  ja: {
    "tutorial": {
      "title": "はじめに",
      "step1Title": "AIインタビュアーを設定する",
      "step1Body": "ここで、参加者にインタビューを行うAIを設定します — 質問内容、ペルソナ、フォローアップの方法を構成できます。",
      "step2Title": "音声かテキストと言語を選ぶ",
      "step2Body": "参加者の回答方法を選びましょう — テキスト、音声からテキスト、またはリアルタイム音声 — そしてAIの声とインタビュー言語を設定します。",
      "step3Title": "リンクをコピーして共有する",
      "step3Body": "共有リンクのセクションから、インタビューのリンクを参加者と共有しましょう。AIが各インタビューを実施し、自動で文字起こしします。",
      "step4Title": "結果が集まる様子を見る",
      "step4Body": "参加者が終えるたびに、分析を開いて文字起こしとテーマを確認しましょう — または、より深いコーディングのためにQDAワークスペースへ送りましょう。",
      "back": "戻る",
      "next": "次へ",
      "done": "始める",
      "skip": "スキップ",
      "help": "使い方"
    },
    "loading": "読み込み中...",
    "sections": {
      "overview": "プロジェクト概要",
      "outline": "インタビューアウトライン",
      "participants": "参加者",
      "data": "インタビューデータ",
      "settings": "プロジェクト設定",
      "title": "API設定",
      "recordingOptions": "録音オプション",
      "interviewSettings": "インタビュー設定",
      "generalSettings": "共通設定",
      "standardModeSettings": "標準モード設定",
      "outlineHelp": "アウトラインの書き方でお困りですか？インタビューガイドを見る",
      "customForm": "カスタムインタビューフォーム",
      "projectSettings": "プロジェクト設定",
      "contentConfiguration": "コンテンツ設定",
      "dataAnalytics": "データ＆分析",
      "customFormDescription": "インタビュー開始前に、インタビュー対象者から追加情報を収集するためのカスタムフォームを作成します。",
      "shareLinks": "インタビューリンクを共有",
      "navigation": "ナビゲーション",
      "digitalTwinConsent": "デジタルツインの同意を収集",
      "digitalTwinConsentDescription": "有効にすると、参加者はインタビューデータを将来の研究のためのAIペルソナ（デジタルツイン）の作成に使用することに同意するかどうかを尋ねられます。"
    },
    "formBuilder": {
      "title": "フォームビルダー",
      "previewForm": "フォームをプレビュー",
      "saveForm": "フォームを保存",
      "saving": "保存中...",
      "formFields": "フォームフィールド",
      "noFieldsTitle": "カスタムフィールドがまだ追加されていません",
      "noFieldsDesc1": "デフォルトフォーム（名前、年齢、性別、職業、学歴、連絡先）を使用するには空白のままにしてください",
      "noFieldsDesc2": "または上のフィールドタイプをクリックしてカスタムインタビューフォームを作成してください",
      "addFirstField": "最初のフィールドを追加",
      "categories": {
        "input": "入力フィールド",
        "choice": "選択フィールド",
        "display": "表示要素"
      },
      "fieldTypes": {
        "text": "テキスト入力",
        "textarea": "長文テキスト",
        "email": "メール",
        "phone": "電話",
        "number": "数字",
        "date": "日付",
        "file": "ファイルアップロード",
        "select": "ドロップダウン",
        "radio": "ラジオボタン",
        "checkbox": "チェックボックス",
        "display_text": "テキストブロック",
        "display_image": "画像",
        "display_video": "動画",
        "section_header": "セクションヘッダー",
        "page_break": "改ページ"
      },
      "fieldDescriptions": {
        "text": "一行テキスト入力",
        "textarea": "複数行テキストエリア",
        "email": "メールアドレス入力",
        "phone": "電話番号入力",
        "number": "数値入力",
        "date": "日付ピッカー",
        "file": "ファイルアップロードフィールド",
        "select": "ドロップダウン選択",
        "radio": "選択肢からの単一選択",
        "checkbox": "複数選択オプション",
        "display_text": "テキストまたはHTMLを表示",
        "display_image": "画像を表示",
        "display_video": "動画コンテンツを埋め込む",
        "section_header": "セクションタイトル",
        "page_break": "新しいページを開始"
      },
      "fieldEditor": {
        "title": "フィールドプロパティ",
        "fieldLabel": "フィールドラベル",
        "fieldType": "フィールドタイプ",
        "placeholderText": "プレースホルダーテキスト",
        "placeholderHelp": "プレースホルダーテキストは入力フィールド内にヒントとして表示されます。入力を開始すると消えます。",
        "helpText": "ヘルプテキスト",
        "helpTextHelper": "ユーザーをガイドするためのオプションのヘルプテキスト",
        "required": "必須フィールド",
        "options": "オプション",
        "addOption": "オプションを追加",
        "removeOption": "オプションを削除",
        "imageUrl": "画像URL",
        "altText": "代替テキスト",
        "videoUrl": "動画URL",
        "videoUrlHelper": "YouTube、Vimeo、または直接動画ファイルURL",
        "displayContent": "表示コンテンツ",
        "displayContentHelper": "基本的なHTMLタグをサポート",
        "textareaRows": "行数",
        "fileAccept": "受け入れるファイルタイプ",
        "maxFileSize": "最大ファイルサイズ (MB)"
      },
      "defaults": {
        "displayTextContent": "表示テキストをここに...",
        "sectionTitle": "セクションタイトル"
      },
      "validation": {
        "emailAutoApplied": "メール検証が自動的に適用されます",
        "numberAutoApplied": "数値検証が自動的に適用されます",
        "phoneAutoApplied": "電話番号検証が自動的に適用されます"
      },
      "actions": {
        "duplicate": "複製",
        "moveUp": "上へ移動",
        "moveDown": "下へ移動",
        "delete": "削除",
        "close": "閉じる"
      },
      "labels": {
        "untitledField": "無題のフィールド",
        "required": "必須",
        "newField": "新規"
      }
    },
    "fields": {
      "outlineTitle": "インタビューアウトライン",
      "apiConfig": "API設定",
      "participantInfo": "参加者情報",
      "recordAudio": "音声録音",
      "hideIntervieweeTranscript": "インタビュー対象者の文字起こしテキストを非表示",
      "hideIntervieweeTranscriptHint": "有効にすると、インタビュー対象者にはAIインタビュアーのメッセージのみが表示され、自分の音声の文字起こしは表示されません。",
      "recordVideo": "動画録画",
      "voiceEnabled": "AI音声インタビュアー",
      "interactionMode": "インタラクションモード",
      "aiVoice": "AI音声",
      "maxDuration": "最大インタビュー時間",
      "name": "名前",
      "age": "年齢",
      "gender": "性別",
      "occupation": "職業",
      "education": "学歴",
      "interviewType": "インタビュータイプ",
      "language": "言語",
      "transcriptionEngine": "文字起こしエンジン",
      "aiInterviewerName": "AIインタビュアー名",
      "aiInterviewerNamePlaceholder": "例：田中博士、研究アシスタント",
      "aiInterviewerNameHelper": "空欄にするとインタビュー言語に基づくデフォルト名が使用されます",
      "interviewerMode": "AIインタビュアーモード",
      "interviewerModeHelp": "このインタビューを駆動するAIモデルの数を選択します。",
      "probingDepth": "深掘りの強度",
      "probingDepthHelp": "回答ごとに AI がどれだけ深掘りするか。"
    },
    "probingDepth": {
      "lightLabel": "軽め",
      "lightDesc": "追加質問 0〜1 回 · 大綱の網羅を優先 · 速いペース",
      "moderateLabel": "中程度（推奨）",
      "moderateDesc": "追加質問 1〜2 回 · 深掘りと網羅のバランス",
      "deepLabel": "深め",
      "deepDesc": "追加質問 2〜4 回 · 動機や具体例まで掘り下げる"
    },
    "interviewerModes": {
      "singleLabel": "シングルモデル（高速）",
      "singleDesc": "1つのAIがインタビュー全体を担当します。低レイテンシ・低コスト。",
      "dualLabel": "デュアルモデル（推奨）",
      "dualDesc": "監督モデルが応答モデルを監査し、より高品質なフォローアップを行います。",
      "structuredNotice": "構造化インタビューは常にデュアルモデルシステムを使用します。"
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2（推奨、最高精度）",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — 99 言語で最高の多言語認識精度。失敗時は自動的に Google → Whisper → Xunfei にフォールバックします。",
      "autoSelectedElevenLabs": "✓ ElevenLabs が自動選択されました",
      "voicePicker": {
        "autoLabel": "自動（推奨）",
        "autoDesc": "ElevenLabs Bella · 多言語対応の女性声 · 全 10 言語対応",
        "warmFemaleMultilingual": "温かみのある女性声 · 多言語対応",
        "calmFemaleMultilingual": "落ち着いた女性声 · 多言語対応",
        "youngFemaleMultilingual": "若々しい女性声 · 多言語対応",
        "matureMaleMultilingual": "成熟した男性声 · 多言語対応",
        "warmMaleMultilingual": "温かみのある男性声 · 多言語対応",
        "casualMaleMultilingual": "カジュアルな男性声 · 多言語対応",
        "mimoDesc": "ネイティブ中国語の女性声 · 普通話に最適",
        "openaiDesc": "ニュートラルな声 · 多言語対応",
        "xunfeiDesc": "中国語女性声（旧）",
        "recommendedBadge": "おすすめ",
        "orPickSpecific": "または特定の声を選択：",
        "filterAll": "すべての声",
        "filterFemale": "女性",
        "filterMale": "男性",
        "filterChinese": "中国語に最適",
        "noMatch": "このフィルターに一致する声はありません。"
      },
      "google": "Google Speech（推奨、最高精度）",
      "whisper": "Whisper（高速、良好な精度）",
      "xunfei": "Xunfei（低速、現在は非推奨）",
      "helperChinese": "Google Speech（推奨）— 北京語で最高精度。Whisperはより高速で安価。Xunfeiは予備用のみ。",
      "helperDefault": "Whisper（推奨）— 高速かつ低コスト。Google Speechはより高精度ですがコストも高めです。",
      "autoSelectedXunfei": "✓ 中国語のためXunfeiが自動選択されました",
      "autoSelectedGoogle": "✓ 中国語のためGoogleが自動選択されました",
      "autoSelectedWhisper": "✓ Whisperが自動選択されました"
    },
    "interactionModes": {
      "textToText": "テキストからテキスト",
      "textToVoice": "テキストから音声＋テキスト",
      "voiceToText": "音声からテキスト",
      "voiceToVoice": "音声から音声＋テキスト",
      "textToTextDesc": "回答を入力し、AIの回答を読む（音声なし）",
      "textToVoiceDesc": "回答を入力し、AIが音声＋テキストで回答",
      "voiceToTextDesc": "回答を話し、AIの回答を読む（音声なし）",
      "voiceToVoiceDesc": "回答を話し、AIが音声＋テキストで回答"
    },
    "actions": {
      "saveOutline": "アウトラインを保存",
      "updateApiKey": "APIキーを更新",
      "downloadData": "インタビューデータをダウンロード",
      "liveSupervision": "ライブ監督",
      "downloadCsv": "CSVをダウンロード",
      "deleteParticipant": "参加者を削除",
      "addKey": "APIキーを追加",
      "saveRecordingPreferences": "録音設定を保存",
      "saveInterviewSettings": "インタビュー設定を保存",
      "saveGeneralSettings": "共通設定を保存",
      "saveStandardModeSettings": "標準モード設定を保存",
      "viewInterviewGuide": "インタビューガイドを見る",
      "editProjectName": "プロジェクト名を編集",
      "findParticipants": "参加者を探す",
      "testWithSynthetic": "合成人間でテスト",
      "classicView": "クラシック表示",
      "newView": "新レイアウト"
    },
    "interviewTypes": {
      "structured": "構造化インタビュー",
      "semiStructured": "半構造化インタビュー"
    },
    "languages": {
      "english": "英語 (English)",
      "chinese": "中国語 (Chinese)",
      "french": "フランス語 (Français)",
      "norwegian": "ノルウェー語 (Norsk)",
      "dutch": "オランダ語 (Nederlands)",
      "german": "ドイツ語 (Deutsch)",
      "spanish": "スペイン語 (Español)",
      "portuguese": "ポルトガル語",
      "japanese": "日本語",
      "arabic": "アラビア語",
      "flexible": "柔軟",
      "flexibleHelp": "インタビュー対象者が希望する言語を選択できるようにします"
    },
    "messages": {
      "outlineSaved": "アウトラインが正常に保存されました",
      "outlineSaveFailed": "アウトラインの保存に失敗しました",
      "saveFailed": "アウトラインの保存に失敗しました",
      "apiKeyUpdated": "APIキーが正常に更新されました",
      "apiKeyError": "APIキーの更新に失敗しました",
      "downloadStarted": "ダウンロード開始",
      "downloadComplete": "ダウンロード完了",
      "downloadFailed": "ダウンロード失敗",
      "noParticipants": "参加者が見つかりません",
      "deleteConfirm": "この参加者を削除しますか？",
      "deleteSuccess": "参加者が正常に削除されました",
      "deleteFailed": "参加者の削除に失敗しました",
      "notFound": "プロジェクトが見つかりません",
      "noKey": "APIキーが設定されていません",
      "settingsSaved": "インタビュー設定が正常に保存されました",
      "settingsScopeNote": "これらの設定は標準モード（Standard Mode）のインタビューリンクにのみ適用されます。リアルタイム音声（Realtime Audio、実験的）のリンクには、独自の音声、応答のテンポ、中断の設定があります（上記のリアルタイム音声モードパネル内）。インタラクションモード、文字起こしエンジン、インタビュアーモード、深掘りの深さ、AIインタビュアー名は、標準モード（Standard Mode）にのみ適用されます。",
      "generalSettingsScopeNote": "これらの設定は標準モードのインタビューリンクとリアルタイム音声（実験版）リンクの両方に適用されます。",
      "settingsError": "インタビュー設定の保存に失敗しました",
      "loadError": "プロジェクトの詳細を読み込めませんでした。後ほど再試行してください",
      "timeoutError": "リクエストタイムアウト - 接続を確認してください",
      "fetchError": "プロジェクトの詳細の読み込みに失敗しました",
      "participantsTimeout": "タイムアウトのため参加者データの読み込みに失敗しました。接続を確認してください",
      "participantsError": "参加者の取得に失敗しました",
      "participantsPagination": "合計 {{total}} 名の参加者、{{pages}} ページ中 {{page}} ページ目",
      "dataVisualization": "データ探索",
      "themeAnalysisLens": "ThemeLensを使用したテーマ分析",
      "inductiveCoding": "AI帰納的コーディング",
      "optimizeOutline": "AIでアウトラインを最適化",
      "optimizationResult": "最適化結果",
      "optimizedOutline": "最適化されたアウトライン：",
      "improvements": "主な改善点：",
      "suggestions": "提案：",
      "applyOptimization": "最適化を適用",
      "startOptimization": "最適化を開始",
      "optimizationGoals": "最適化の目標（任意）",
      "optimizationGoalsPlaceholder": "例：質問の深さを増す、質問の開放性を高める、質問のロジックを最適化するなど",
      "unsavedChanges": "保存されていない変更があります",
      "changesDiscarded": "変更が破棄されました",
      "optimizing": "最適化中...",
      "recordingPreferencesSaved": "録音設定が保存されました",
      "recordingPreferencesSaveFailed": "録音設定の保存に失敗しました",
      "outlineRequired": "アウトラインは必須です",
      "outlineOptimized": "アウトラインが最適化されました",
      "outlineOptimizeFailed": "アウトラインの最適化に失敗しました",
      "optimizedOutlineApplied": "最適化されたアウトラインが適用されました",
      "projectNameSaved": "プロジェクト名が正常に保存されました",
      "projectNameSaveFailed": "プロジェクト名の保存に失敗しました",
      "unsavedProjectNameChanges": "プロジェクト名に保存されていない変更があります",
      "shareLinkInfo": "これらのリンクをインタビュー対象者と共有してください。ログインなしで直接インタビューにアクセスできます。",
      "standardLinkCopied": "標準モードリンクをクリップボードにコピーしました",
      "realtimeLinkCopied": "リアルタイムモードリンクをクリップボードにコピーしました",
      "selectInterviewFirst": "最初にインタビューを選択してください。"
    },
    "downloads": {
      "title": "ダウンロードオプション",
      "description": "ダウンロードするものを選択してください：",
      "downloadEverything": "すべてダウンロード",
      "downloadMetadataOnly": "メタデータのみダウンロード",
      "downloadAudioForSpecificInterview": "特定のインタビューの音声をダウンロード",
      "interviewLabel": "インタビュー",
      "downloadSelectedInterviewAudio": "選択したインタビューの音声をダウンロード"
    },
    "warnings": {
      "audioRecordingEnabled": "警告：このプロジェクトでは音声録音が有効になっています。インタビュー中に参加者の声が録音されます。",
      "audioInterviewNotice": "⚠️ 実験的な機能：これは精度が低い新しい技術です。"
    },
    "buttons": {
      "testWithSynthetic": "合成人間とのインタビューをテスト"
    },
    "linkTypes": {
      "standardMode": "標準インタビューモード",
      "standardDescription": "4つのモダリティをサポート（音声から音声、音声からテキスト、テキストから音声、テキストからテキスト）。推奨。",
      "realtimeMode": "リアルタイム音声モード（実験的）",
      "realtimeDescription": "リアルタイムAI会話による音声ベースのインタビュー。新技術。"
    },
    "realtimeSettings": {
      "title": "リアルタイム音声設定",
      "description": "リアルタイム音声インタビューをカスタマイズします。初期値は以前のバージョンと同じです。",
      "voiceLabel": "AIの音声",
      "voiceDefaultTag": "デフォルト",
      "voiceNaturalTag": "より自然",
      "pacingLabel": "応答のテンポ",
      "pacingQuick": "クイック — 素早く応答",
      "pacingStandard": "標準（デフォルト）",
      "pacingPatient": "ゆっくり — 長めに待つ",
      "pacingHelp": "参加者が話し終えてから、AIが応答するまで待つ時間です。",
      "allowInterruptionLabel": "参加者によるAIの中断を許可する",
      "allowInterruptionHelp": "オン：参加者はAIの発話に重ねて話せます。オフ：AIは話し終えてから聞き取りを開始し、ノートパソコンのスピーカーからのエコーで中断されるのを防ぎます。",
      "save": "リアルタイム設定を保存",
      "saved": "リアルタイム設定を保存しました",
      "saveFailed": "リアルタイム設定の保存に失敗しました"
    },
    "currentSettings": "現在の設定：{{type}}、言語：{{language}}",
    "collaboration": {
      "title": "チーム",
      "invite": "コラボレーターを招待",
      "inviteLabel": "メールアドレス",
      "inviteButton": "招待する",
      "inviting": "送信中...",
      "inviteSuccess": "招待を送信しました！",
      "inviteError": "招待の送信に失敗しました。もう一度お試しください。",
      "alreadyInvited": "このメールはすでに招待されています。",
      "collaborators": "コラボレーター",
      "noCollaborators": "まだコラボレーターはいません。誰かを招待して始めましょう。",
      "statusPending": "保留中",
      "statusActive": "参加中",
      "remove": "削除",
      "removeConfirm": "このコラボレーターを削除しますか？",
      "sharedBy": "{{owner}} が共有",
      "surveyHint": "このプロジェクトに人を招待します。共同編集者はすべてのアンケートを閲覧・編集できます。",
      "close": "閉じる"
    }
  },
  ar: {
    "tutorial": {
      "title": "البدء",
      "step1Title": "إعداد المُحاوِر الذكي",
      "step1Body": "هنا تقوم بتهيئة الذكاء الاصطناعي الذي يُجري المقابلات مع المشاركين — أسئلته وشخصيته وكيفية متابعته للأسئلة.",
      "step2Title": "اختر الصوت أو النص واللغة",
      "step2Body": "حدّد كيفية رد المشاركين — نصًا، أو تحويل الصوت إلى نص، أو صوتًا في الوقت الفعلي — واضبط صوت الذكاء الاصطناعي ولغة المقابلة.",
      "step3Title": "انسخ رابطك وشاركه",
      "step3Body": "شارك رابط مقابلتك مع المشاركين من قسم روابط المشاركة. يُجري الذكاء الاصطناعي كل مقابلة ويُفرّغها تلقائيًا.",
      "step4Title": "تابع وصول النتائج",
      "step4Body": "كلما أنهى المشاركون، افتح التحليلات لمراجعة النصوص والمواضيع — أو أرسلها إلى مساحة عمل QDA لترميز أعمق.",
      "back": "رجوع",
      "next": "التالي",
      "done": "ابدأ",
      "skip": "تخطٍ",
      "help": "كيف يعمل"
    },
    "loading": "جارٍ التحميل...",
    "sections": {
      "overview": "نظرة عامة على المشروع",
      "outline": "مخطط المقابلة",
      "participants": "المشاركون",
      "data": "بيانات المقابلة",
      "settings": "إعدادات المشروع",
      "title": "تكوين API",
      "recordingOptions": "خيارات التسجيل",
      "interviewSettings": "إعدادات المقابلة",
      "generalSettings": "الإعدادات العامة",
      "standardModeSettings": "إعدادات الوضع القياسي",
      "outlineHelp": "هل تحتاج مساعدة في كتابة مخططك؟ عرض دليل المقابلة",
      "customForm": "نموذج مقابلة مخصص",
      "projectSettings": "إعدادات المشروع",
      "contentConfiguration": "تكوين المحتوى",
      "dataAnalytics": "البيانات والتحليلات",
      "customFormDescription": "أنشئ نموذجاً مخصصاً لجمع معلومات إضافية من المقابَلين قبل بدء المقابلة.",
      "shareLinks": "مشاركة روابط المقابلة",
      "navigation": "التنقل",
      "digitalTwinConsent": "جمع موافقة التوأم الرقمي",
      "digitalTwinConsentDescription": "عند التفعيل، سيُسأل المشاركون عما إذا كانوا يوافقون على استخدام بيانات مقابلتهم لإنشاء شخصية ذكاء اصطناعي (توأم رقمي) للبحث المستقبلي."
    },
    "formBuilder": {
      "title": "منشئ النماذج",
      "previewForm": "معاينة النموذج",
      "saveForm": "حفظ النموذج",
      "saving": "جارٍ الحفظ...",
      "formFields": "حقول النموذج",
      "noFieldsTitle": "لم تتم إضافة حقول مخصصة بعد",
      "noFieldsDesc1": "اتركه فارغاً لاستخدام النموذج الافتراضي (الاسم، العمر، الجنس، المهنة، التعليم، التواصل)",
      "noFieldsDesc2": "أو انقر على أي نوع حقل أعلاه لإنشاء نموذج مقابلة مخصص",
      "addFirstField": "أضف حقلك الأول",
      "categories": {
        "input": "حقول الإدخال",
        "choice": "حقول الاختيار",
        "display": "عناصر العرض"
      },
      "fieldTypes": {
        "text": "إدخال نصي",
        "textarea": "نص طويل",
        "email": "البريد الإلكتروني",
        "phone": "الهاتف",
        "number": "الرقم",
        "date": "التاريخ",
        "file": "رفع ملف",
        "select": "قائمة منسدلة",
        "radio": "أزرار الاختيار",
        "checkbox": "مربعات الاختيار",
        "display_text": "كتلة نصية",
        "display_image": "صورة",
        "display_video": "فيديو",
        "section_header": "رأس قسم",
        "page_break": "فاصل صفحة"
      },
      "fieldDescriptions": {
        "text": "إدخال نصي من سطر واحد",
        "textarea": "منطقة نص متعددة الأسطر",
        "email": "إدخال عنوان البريد الإلكتروني",
        "phone": "إدخال رقم الهاتف",
        "number": "إدخال رقمي",
        "date": "منتقي التاريخ",
        "file": "حقل رفع الملف",
        "select": "اختيار منسدل",
        "radio": "اختيار واحد من الخيارات",
        "checkbox": "خيارات متعددة",
        "display_text": "عرض نص أو HTML",
        "display_image": "عرض صورة",
        "display_video": "تضمين محتوى فيديو",
        "section_header": "عنوان القسم",
        "page_break": "بدء صفحة جديدة"
      },
      "fieldEditor": {
        "title": "خصائص الحقل",
        "fieldLabel": "تسمية الحقل",
        "fieldType": "نوع الحقل",
        "placeholderText": "نص العنصر النائب",
        "placeholderHelp": "يظهر نص العنصر النائب داخل حقل الإدخال كتلميح للمستخدمين. يختفي عند بدء الكتابة.",
        "helpText": "نص المساعدة",
        "helpTextHelper": "نص مساعدة اختياري لتوجيه المستخدمين",
        "required": "حقل مطلوب",
        "options": "الخيارات",
        "addOption": "إضافة خيار",
        "removeOption": "إزالة خيار",
        "imageUrl": "رابط الصورة",
        "altText": "النص البديل",
        "videoUrl": "رابط الفيديو",
        "videoUrlHelper": "YouTube أو Vimeo أو رابط ملف فيديو مباشر",
        "displayContent": "محتوى العرض",
        "displayContentHelper": "يدعم وسوم HTML الأساسية",
        "textareaRows": "عدد الصفوف",
        "fileAccept": "أنواع الملفات المقبولة",
        "maxFileSize": "الحجم الأقصى للملف (MB)"
      },
      "defaults": {
        "displayTextContent": "نص العرض الخاص بك هنا...",
        "sectionTitle": "عنوان القسم"
      },
      "validation": {
        "emailAutoApplied": "سيتم تطبيق التحقق من البريد الإلكتروني تلقائياً",
        "numberAutoApplied": "سيتم تطبيق التحقق من الأرقام تلقائياً",
        "phoneAutoApplied": "سيتم تطبيق التحقق من رقم الهاتف تلقائياً"
      },
      "actions": {
        "duplicate": "تكرار",
        "moveUp": "تحريك للأعلى",
        "moveDown": "تحريك للأسفل",
        "delete": "حذف",
        "close": "إغلاق"
      },
      "labels": {
        "untitledField": "حقل بدون عنوان",
        "required": "مطلوب",
        "newField": "جديد"
      }
    },
    "fields": {
      "outlineTitle": "مخطط المقابلة",
      "apiConfig": "تكوين API",
      "participantInfo": "معلومات المشارك",
      "recordAudio": "تسجيل الصوت",
      "hideIntervieweeTranscript": "إخفاء النص المكتوب للمستجوب",
      "hideIntervieweeTranscriptHint": "عند التفعيل، سيرى المستجوبون فقط رسائل المحاور الذكي، وليس نصهم المنطوق المكتوب.",
      "recordVideo": "تسجيل الفيديو",
      "voiceEnabled": "مقابل صوتي بالذكاء الاصطناعي",
      "interactionMode": "وضع التفاعل",
      "aiVoice": "صوت الذكاء الاصطناعي",
      "maxDuration": "مدة المقابلة القصوى",
      "name": "الاسم",
      "age": "العمر",
      "gender": "الجنس",
      "occupation": "المهنة",
      "education": "التعليم",
      "interviewType": "نوع المقابلة",
      "language": "اللغة",
      "transcriptionEngine": "محرك النسخ",
      "aiInterviewerName": "اسم المحاور الذكي",
      "aiInterviewerNamePlaceholder": "مثال: د. أحمد، مساعد بحثي",
      "aiInterviewerNameHelper": "اتركه فارغاً لاستخدام الاسم الافتراضي بناءً على لغة المقابلة",
      "interviewerMode": "وضع مُجري المقابلة بالذكاء الاصطناعي",
      "interviewerModeHelp": "اختر عدد نماذج الذكاء الاصطناعي التي تدير هذه المقابلة.",
      "probingDepth": "عمق المتابعة",
      "probingDepthHelp": "مدى متابعة الذكاء الاصطناعي لكل إجابة بأسئلة إضافية."
    },
    "probingDepth": {
      "lightLabel": "خفيفة",
      "lightDesc": "0-1 سؤال متابعة · أولوية للتغطية · إيقاع سريع",
      "moderateLabel": "متوسطة (موصى بها)",
      "moderateDesc": "1-2 أسئلة متابعة · توازن بين العمق والتغطية",
      "deepLabel": "عميقة",
      "deepDesc": "2-4 أسئلة متابعة · يتعمّق في الدوافع والأمثلة"
    },
    "interviewerModes": {
      "singleLabel": "نموذج واحد (أسرع)",
      "singleDesc": "ذكاء اصطناعي واحد يدير المقابلة بأكملها. زمن استجابة وتكلفة أقل.",
      "dualLabel": "نموذجان (موصى به)",
      "dualDesc": "نموذج مشرف يدقق نموذج الاستجابة لأسئلة متابعة أعلى جودة.",
      "structuredNotice": "تستخدم المقابلات المنظمة دائمًا نظام النموذجين."
    },
    "transcriptionEngine": {
      "elevenlabs": "ElevenLabs Scribe v2 (موصى به، أعلى دقة)",
      "helperWithElevenLabs": "ElevenLabs Scribe v2 — أفضل دقة متعددة اللغات عبر 99 لغة. يتراجع تلقائياً إلى Google → Whisper → Xunfei عند الحاجة.",
      "autoSelectedElevenLabs": "✓ تم اختيار ElevenLabs تلقائياً",
      "voicePicker": {
        "autoLabel": "تلقائي (موصى به)",
        "autoDesc": "ElevenLabs Bella · صوت أنثوي متعدد اللغات · لجميع 10 لغات",
        "warmFemaleMultilingual": "صوت أنثوي دافئ · متعدد اللغات",
        "calmFemaleMultilingual": "صوت أنثوي هادئ · متعدد اللغات",
        "youngFemaleMultilingual": "صوت أنثوي شاب · متعدد اللغات",
        "matureMaleMultilingual": "صوت ذكوري ناضج · متعدد اللغات",
        "warmMaleMultilingual": "صوت ذكوري دافئ · متعدد اللغات",
        "casualMaleMultilingual": "صوت ذكوري عفوي · متعدد اللغات",
        "mimoDesc": "صوت أنثوي صيني أصلي · الأفضل للماندرين",
        "openaiDesc": "صوت محايد · متعدد اللغات",
        "xunfeiDesc": "صوت أنثوي صيني (قديم)",
        "recommendedBadge": "موصى به",
        "orPickSpecific": "أو اختر صوتًا محددًا:",
        "filterAll": "جميع الأصوات",
        "filterFemale": "أنثوي",
        "filterMale": "ذكوري",
        "filterChinese": "الأفضل للصينية",
        "noMatch": "لا توجد أصوات تطابق هذا الفلتر."
      },
      "google": "Google Speech (موصى به، أعلى دقة)",
      "whisper": "Whisper (سريع، دقة جيدة)",
      "xunfei": "Xunfei (بطيء، لم يعد موصى به)",
      "helperChinese": "Google Speech (موصى به) — أعلى دقة للصينية الفصحى. Whisper أسرع وأرخص. Xunfei يُحتفظ به كخيار احتياطي فقط.",
      "helperDefault": "Whisper (موصى به) — سريع ومنخفض التكلفة. Google Speech يقدم دقة أعلى مقابل تكلفة أعلى.",
      "autoSelectedXunfei": "✓ تم اختيار Xunfei تلقائياً للصينية",
      "autoSelectedGoogle": "✓ تم اختيار Google تلقائياً للصينية",
      "autoSelectedWhisper": "✓ تم اختيار Whisper تلقائياً"
    },
    "interactionModes": {
      "textToText": "نص إلى نص",
      "textToVoice": "نص إلى صوت + نص",
      "voiceToText": "صوت إلى نص",
      "voiceToVoice": "صوت إلى صوت + نص",
      "textToTextDesc": "اكتب إجاباتك، اقرأ ردود الذكاء الاصطناعي (بدون صوت)",
      "textToVoiceDesc": "اكتب إجاباتك، يرد الذكاء الاصطناعي بصوت + نص",
      "voiceToTextDesc": "تحدث بإجاباتك، اقرأ ردود الذكاء الاصطناعي (بدون صوت)",
      "voiceToVoiceDesc": "تحدث بإجاباتك، يرد الذكاء الاصطناعي بصوت + نص"
    },
    "actions": {
      "saveOutline": "حفظ المخطط",
      "updateApiKey": "تحديث مفتاح API",
      "downloadData": "تنزيل بيانات المقابلة",
      "liveSupervision": "الإشراف المباشر",
      "downloadCsv": "تنزيل CSV",
      "deleteParticipant": "حذف المشارك",
      "addKey": "إضافة مفتاح API",
      "saveRecordingPreferences": "حفظ تفضيلات التسجيل",
      "saveInterviewSettings": "حفظ إعدادات المقابلة",
      "saveGeneralSettings": "حفظ الإعدادات العامة",
      "saveStandardModeSettings": "حفظ إعدادات الوضع القياسي",
      "viewInterviewGuide": "عرض دليل المقابلة",
      "editProjectName": "تعديل اسم المشروع",
      "findParticipants": "إيجاد المشاركين",
      "testWithSynthetic": "اختبار مع إنسان اصطناعي",
      "classicView": "العرض الكلاسيكي",
      "newView": "التصميم الجديد"
    },
    "interviewTypes": {
      "structured": "مقابلة منظمة",
      "semiStructured": "مقابلة شبه منظمة"
    },
    "languages": {
      "english": "الإنجليزية",
      "chinese": "الصينية",
      "french": "الفرنسية (Français)",
      "norwegian": "النرويجية (Norsk)",
      "dutch": "الهولندية (Nederlands)",
      "german": "الألمانية (Deutsch)",
      "spanish": "الإسبانية (Español)",
      "portuguese": "البرتغالية",
      "japanese": "اليابانية",
      "arabic": "العربية",
      "flexible": "مرن",
      "flexibleHelp": "اسمح للمقابَل باختيار لغته المفضلة"
    },
    "messages": {
      "outlineSaved": "تم حفظ المخطط بنجاح",
      "outlineSaveFailed": "فشل في حفظ المخطط",
      "saveFailed": "فشل في حفظ المخطط",
      "apiKeyUpdated": "تم تحديث مفتاح API بنجاح",
      "apiKeyError": "فشل في تحديث مفتاح API",
      "downloadStarted": "بدأ التنزيل",
      "downloadComplete": "اكتمل التنزيل",
      "downloadFailed": "فشل التنزيل",
      "noParticipants": "لم يتم العثور على مشاركين",
      "deleteConfirm": "هل أنت متأكد أنك تريد حذف هذا المشارك؟",
      "deleteSuccess": "تم حذف المشارك بنجاح",
      "deleteFailed": "فشل في حذف المشارك",
      "notFound": "المشروع غير موجود",
      "noKey": "مفتاح API غير مكوّن",
      "settingsSaved": "تم حفظ إعدادات المقابلة بنجاح",
      "settingsScopeNote": "تنطبق هذه الإعدادات فقط على رابط مقابلة الوضع القياسي (Standard Mode). أما رابط الصوت في الوقت الفعلي (Realtime Audio) (تجريبي) فله إعداداته الخاصة بالصوت وإيقاع الاستجابة والمقاطعة (في لوحة وضع الصوت في الوقت الفعلي أعلاه)؛ ووضع التفاعل، ومحرك النسخ، ووضع المُحاوِر، وعمق الاستقصاء، واسم المُحاوِر بالذكاء الاصطناعي تنطبق على الوضع القياسي (Standard Mode) فقط.",
      "generalSettingsScopeNote": "تنطبق هذه الإعدادات على كل من رابط المقابلة بالوضع القياسي ورابط الصوت الفوري (التجريبي).",
      "settingsError": "فشل في حفظ إعدادات المقابلة",
      "loadError": "فشل في تحميل تفاصيل المشروع، يرجى المحاولة مرة أخرى لاحقاً",
      "timeoutError": "انتهت مهلة الطلب - يرجى التحقق من اتصالك",
      "fetchError": "فشل في تحميل تفاصيل المشروع",
      "participantsTimeout": "فشل في تحميل بيانات المشاركين بسبب انتهاء المهلة، يرجى التحقق من اتصالك",
      "participantsError": "فشل في جلب المشاركين",
      "participantsPagination": "إجمالي {{total}} مشارك، الصفحة {{page}} من {{pages}}",
      "dataVisualization": "استكشاف البيانات",
      "themeAnalysisLens": "تحليل الموضوعات باستخدام ThemeLens",
      "inductiveCoding": "الترميز الاستقرائي بالذكاء الاصطناعي",
      "optimizeOutline": "تحسين المخطط بالذكاء الاصطناعي",
      "optimizationResult": "نتيجة التحسين",
      "optimizedOutline": "المخطط المحسّن:",
      "improvements": "التحسينات الرئيسية:",
      "suggestions": "الاقتراحات:",
      "applyOptimization": "تطبيق التحسين",
      "startOptimization": "بدء التحسين",
      "optimizationGoals": "أهداف التحسين (اختياري)",
      "optimizationGoalsPlaceholder": "مثال: زيادة عمق الأسئلة، تحسين انفتاح الأسئلة، تحسين منطق الأسئلة، إلخ.",
      "unsavedChanges": "لديك تغييرات غير محفوظة",
      "changesDiscarded": "تم تجاهل التغييرات",
      "optimizing": "جارٍ التحسين...",
      "recordingPreferencesSaved": "تم حفظ تفضيلات التسجيل",
      "recordingPreferencesSaveFailed": "فشل في حفظ تفضيلات التسجيل",
      "outlineRequired": "المخطط مطلوب",
      "outlineOptimized": "تم تحسين المخطط",
      "outlineOptimizeFailed": "فشل في تحسين المخطط",
      "optimizedOutlineApplied": "تم تطبيق المخطط المحسّن",
      "projectNameSaved": "تم حفظ اسم المشروع بنجاح",
      "projectNameSaveFailed": "فشل في حفظ اسم المشروع",
      "unsavedProjectNameChanges": "لديك تغييرات غير محفوظة في اسم المشروع",
      "shareLinkInfo": "شارك هذه الروابط مع المقابَلين. يمكنهم الوصول إلى المقابلة مباشرة دون تسجيل الدخول.",
      "standardLinkCopied": "تم نسخ رابط الوضع القياسي إلى الحافظة",
      "realtimeLinkCopied": "تم نسخ رابط الوضع الفوري إلى الحافظة",
      "selectInterviewFirst": "يرجى اختيار مقابلة أولاً."
    },
    "downloads": {
      "title": "خيارات التنزيل",
      "description": "اختر ما تريد تنزيله:",
      "downloadEverything": "تنزيل الكل",
      "downloadMetadataOnly": "تنزيل البيانات الوصفية فقط",
      "downloadAudioForSpecificInterview": "تنزيل الصوت لمقابلة محددة",
      "interviewLabel": "مقابلة",
      "downloadSelectedInterviewAudio": "تنزيل صوت المقابلة المحددة"
    },
    "warnings": {
      "audioRecordingEnabled": "تحذير: تسجيل الصوت ممكّن لهذا المشروع. سيتم تسجيل أصوات المشاركين خلال المقابلات.",
      "audioInterviewNotice": "⚠️ ميزة تجريبية: هذه تقنية جديدة بدقة أقل."
    },
    "buttons": {
      "testWithSynthetic": "اختبار المقابلة مع إنسان اصطناعي"
    },
    "linkTypes": {
      "standardMode": "وضع المقابلة القياسي",
      "standardDescription": "يدعم 4 أوضاع (صوت إلى صوت، صوت إلى نص، نص إلى صوت، نص إلى نص). موصى به.",
      "realtimeMode": "وضع الصوت الفوري (تجريبي)",
      "realtimeDescription": "مقابلة صوتية مع محادثة ذكاء اصطناعي فورية. تقنية جديدة."
    },
    "realtimeSettings": {
      "title": "إعدادات الصوت في الوقت الفعلي",
      "description": "خصّص مقابلة الصوت في الوقت الفعلي. الإعدادات الافتراضية مطابقة للإصدار السابق.",
      "voiceLabel": "صوت الذكاء الاصطناعي",
      "voiceDefaultTag": "افتراضي",
      "voiceNaturalTag": "أكثر طبيعية",
      "pacingLabel": "إيقاع الاستجابة",
      "pacingQuick": "سريع — يستجيب بسرعة",
      "pacingStandard": "قياسي (افتراضي)",
      "pacingPatient": "متأنٍّ — ينتظر مدة أطول",
      "pacingHelp": "المدة التي ينتظرها الذكاء الاصطناعي بعد توقف المشارك عن الكلام قبل أن يرد.",
      "allowInterruptionLabel": "السماح للمشاركين بمقاطعة الذكاء الاصطناعي",
      "allowInterruptionHelp": "مُفعَّل: يمكن للمشاركين التحدث أثناء كلام الذكاء الاصطناعي. مُعطَّل: ينهي الذكاء الاصطناعي كلامه قبل أن يستمع — وهذا يمنع مقاطعته بفعل الصدى الصادر من مكبرات صوت الحاسوب المحمول.",
      "save": "حفظ إعدادات الوقت الفعلي",
      "saved": "تم حفظ إعدادات الوقت الفعلي",
      "saveFailed": "تعذّر حفظ إعدادات الوقت الفعلي"
    },
    "currentSettings": "الإعدادات الحالية: {{type}}، اللغة: {{language}}",
    "collaboration": {
      "title": "الفريق",
      "invite": "دعوة متعاون",
      "inviteLabel": "عنوان البريد الإلكتروني",
      "inviteButton": "دعوة",
      "inviting": "جارٍ الإرسال...",
      "inviteSuccess": "تم إرسال الدعوة!",
      "inviteError": "فشل إرسال الدعوة. يرجى المحاولة مرة أخرى.",
      "alreadyInvited": "تم دعوة هذا البريد الإلكتروني بالفعل.",
      "collaborators": "المتعاونون",
      "noCollaborators": "لا يوجد متعاونون بعد. ادعُ شخصاً للبدء.",
      "statusPending": "في الانتظار",
      "statusActive": "نشط",
      "remove": "إزالة",
      "removeConfirm": "إزالة هذا المتعاون؟",
      "sharedBy": "تمت المشاركة بواسطة {{owner}}",
      "surveyHint": "ادعُ أشخاصًا إلى هذا المشروع. يمكن للمتعاونين عرض جميع استطلاعاته وتحريرها.",
      "close": "إغلاق"
    }
  }
};
